import { apiBase as API_BASE, environmentName } from './environment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from './lib/firebase';
import { encryptSubmissionPackage } from './lib/onboardingCrypto';
import PortalExperience from './PortalExperience';
import ComplianceWorkspace from './ComplianceWorkspace';
import { serverDocumentState, formatEvidenceTime } from './complianceEvidence';


const HOSTED_DEV = environmentName === 'dev';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const CHEMCAB_DOMAIN = 'chemcab.com';
const MAX_UPLOAD_MB = 10;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;
const MAX_VEHICLE_PHOTOS = 4;
const DASHBOARD_STORAGE_PREFIX = 'chemcab-driver-dashboard-v2';
const INVITE_STORAGE_KEY = 'chemcab-driver-onboarding-latest-submission';
const DRIVER_PROFILE_SCHEMA_VERSION = 1;
const DASHBOARD_DOCUMENT_ACCEPT = '.pdf,.png,.jpg,.jpeg,.docx,application/pdf,image/png,image/jpeg,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const FALLBACK_BOOTSTRAP_MODULE_IDS = [
  'orientation-core',
  'core-courier-driving-safety',
  'fatigue-hours-service',
];

const DOCUMENT_LIBRARY = [
  {
    id: 'insurance',
    title: 'Proof of insurance',
    description: 'Declarations page or coverage proof for the vehicle used on ChemCab jobs.',
    accept: DASHBOARD_DOCUMENT_ACCEPT,
    reviewGroup: 'Insurance',
  },
  {
    id: 'registration',
    title: 'Vehicle registration',
    description: 'Current registration matching the vehicle profile and plate.',
    accept: DASHBOARD_DOCUMENT_ACCEPT,
    reviewGroup: 'Vehicle',
  },
  {
    id: 'driverLicenseFront',
    title: 'Driver license front',
    description: 'Clear front image of the active license.',
    accept: DASHBOARD_DOCUMENT_ACCEPT,
    reviewGroup: 'Identity',
  },
  {
    id: 'driverLicenseBack',
    title: 'Driver license back',
    description: 'Clear back image of the active license.',
    accept: DASHBOARD_DOCUMENT_ACCEPT,
    reviewGroup: 'Identity',
  },
];

const TRAINING_MODULES = [
  {
    id: 'orientation',
    title: 'ChemCab Orientation',
    subtitle: 'How ChemCab works, who owns what, and when to stop work.',
    estimatedMinutes: 14,
    certificateName: 'ChemCab Orientation Certificate',
    sections: [
      {
        heading: 'ChemCab role and chain of custody',
        bullets: [
          'ChemCab acts as the courier and carrier, while the client remains responsible for classifying and preparing regulated shipments.',
          'Drivers may reject any package that looks damaged, leaking, mislabeled, or outside ChemCab policy.',
          'Drivers never open, sample, relabel, repair, or repackage client materials.',
        ],
      },
      {
        heading: 'Stop-work authority',
        bullets: [
          'Drivers pause the move and escalate when package condition, routing, or client requests create a safety concern.',
          'Escalation must happen before departure if the issue affects transport safety.',
        ],
      },
    ],
    questions: [
      {
        prompt: 'Who is responsible for classifying a hazardous sample before ChemCab transports it?',
        options: [
          'The client or shipper preparing the package',
          'The ChemCab driver at pickup',
          'Whoever is closest to the load at dispatch time',
          'The receiver at dropoff',
        ],
        correctIndex: 0,
        explanation:
          'ChemCab drivers should not improvise classification. The shipper or offeror must classify and prepare the shipment before handoff.',
      },
      {
        prompt: 'What should a ChemCab driver do if a package looks wet, damaged, or improperly presented?',
        options: [
          'Take a picture and transport it anyway',
          'Reject it, stop the move, and escalate',
          'Open it to inspect the contents',
          'Repackage it in the vehicle',
        ],
        correctIndex: 1,
        explanation:
          'Visible damage or improper presentation is a stop-work issue. The correct response is to reject the load and escalate immediately.',
      },
      {
        prompt: 'Which action is always prohibited for ChemCab drivers?',
        options: [
          'Documenting package condition at pickup',
          'Opening or repairing a package',
          'Calling dispatch for guidance',
          'Rejecting an unsafe load',
        ],
        correctIndex: 1,
        explanation:
          'Drivers may document, reject, and escalate. They may not open, repair, relabel, or otherwise alter the shipment.',
      },
    ],
  },
  {
    id: 'safe-driving',
    title: 'Safe Driving and Duty Fitness',
    subtitle: 'Defensive driving, weather, fatigue, parking, and incident response.',
    estimatedMinutes: 18,
    certificateName: 'ChemCab Safe Driving Certificate',
    sections: [
      {
        heading: 'Defensive driving baseline',
        bullets: [
          'Seat belts, speed control, following distance, and low-distraction driving are non-negotiable.',
          'Flooded roads, poor visibility, and fatigue require a slower plan or a stop.',
          'Backing, parking, and site arrival should prioritize low-risk positioning over speed.',
        ],
      },
      {
        heading: 'Incident response',
        bullets: [
          'Protect life first, then secure the scene, call emergency services when needed, and notify ChemCab.',
          'Do not guess through a breakdown or accident when safe escalation is available.',
        ],
      },
    ],
    questions: [
      {
        prompt: 'If a driver feels fatigue affecting reaction time, what is the correct ChemCab response?',
        options: [
          'Keep driving but avoid phone calls',
          'Drive faster to finish sooner',
          'Pause operations and escalate the safety concern',
          'Wait until the next stop to mention it',
        ],
        correctIndex: 2,
        explanation:
          'Fatigue is a fitness-for-duty issue. ChemCab expects drivers to stop and escalate before safety degrades further.',
      },
      {
        prompt: 'What is the best parking choice at a congested client site?',
        options: [
          'Wherever is closest to the door',
          'Any active fire lane as long as hazards are on',
          'The lowest-risk legal position, even if it takes longer',
          'Double parked near the sample handoff',
        ],
        correctIndex: 2,
        explanation:
          'Low-risk legal positioning matters more than speed. Unsafe parking creates avoidable incident exposure.',
      },
      {
        prompt: 'What comes first in an accident response?',
        options: [
          'Explaining the delay to the client',
          'Protecting life and calling for emergency help when needed',
          'Taking marketing photos for documentation',
          'Guessing whether the load can still be delivered',
        ],
        correctIndex: 1,
        explanation:
          'Life safety comes first. Client updates and delivery decisions happen after the scene is stabilized.',
      },
    ],
  },
  {
    id: 'hazmat-awareness',
    title: 'DOT Hazmat Awareness',
    subtitle: 'Labels, markings, shipping papers, limited quantity recognition, and emergency info.',
    estimatedMinutes: 21,
    certificateName: 'DOT Hazmat Awareness Certificate',
    sections: [
      {
        heading: 'Recognition and documentation',
        bullets: [
          'Drivers should recognize common hazard labels, UN and NA numbers, and shipping-paper requirements.',
          'Limited quantity and excepted quantity do not mean drivers can ignore the shipment declaration.',
          'Emergency-response information must remain available while transporting regulated materials.',
        ],
      },
      {
        heading: 'What drivers are expected to verify',
        bullets: [
          'Package appears closed, upright, intact, and consistent with dispatch records.',
          'Required paperwork is present when the package is declared as regulated hazmat.',
        ],
      },
    ],
    questions: [
      {
        prompt: 'Does a small sample quantity automatically mean it is non-hazardous for transport purposes?',
        options: [
          'Yes, small quantity means no hazmat rules apply',
          'No, quantity alone does not remove hazmat obligations',
          'Only if the driver believes it is safe',
          'Only after the package is placed in the trunk',
        ],
        correctIndex: 1,
        explanation:
          'Small quantity does not automatically mean non-hazmat. The classification and any exception must be determined shipment by shipment.',
      },
      {
        prompt: 'What should a driver verify before accepting a declared hazmat package?',
        options: [
          'That the package is intact and the required paperwork is available',
          'That the client wrote a long enough email thread',
          'That the driver has opened the package personally',
          'That dispatch is too busy to help',
        ],
        correctIndex: 0,
        explanation:
          'Drivers verify presentation, condition, and required paperwork. They do not open the package.',
      },
      {
        prompt: 'What should remain accessible while transporting regulated hazardous material?',
        options: [
          'Only the driver phone charger',
          'Shipping papers and emergency-response information',
          'The client invoice only',
          'A handwritten route guess',
        ],
        correctIndex: 1,
        explanation:
          'Shipping papers and emergency information must remain available for regulated hazmat movements.',
      },
    ],
  },
  {
    id: 'courier-ops',
    title: 'Function-Specific Courier Operations',
    subtitle: 'Package acceptance, load securement, routing discipline, and incident escalation.',
    estimatedMinutes: 17,
    certificateName: 'ChemCab Courier Operations Certificate',
    sections: [
      {
        heading: 'Pickup and load checks',
        bullets: [
          'Verify package condition, orientation, securement, and dispatch match before departure.',
          'Reject bulging, corroded, unlabeled, or leaking packages.',
          'Use cargo placement and securement that prevent movement during transport.',
        ],
      },
      {
        heading: 'Incident boundaries',
        bullets: [
          'Drivers do not tighten valves, vent containers, or perform cleanup beyond ChemCab policy.',
          'Leak, spill, fire, or collision events trigger isolation, evacuation when needed, and escalation.',
        ],
      },
    ],
    questions: [
      {
        prompt: 'Which package should always be rejected at pickup?',
        options: [
          'A sealed package that matches dispatch and is dry',
          'A visibly leaking or bulging package',
          'A clearly labeled package in secondary containment',
          'A package that arrived early',
        ],
        correctIndex: 1,
        explanation:
          'Visible leakage, bulging, or package damage is an automatic rejection and escalation event.',
      },
      {
        prompt: 'What is ChemCab policy if a driver notices a leak in transit?',
        options: [
          'Tighten the package and keep moving',
          'Pull over safely, isolate the situation, and escalate immediately',
          'Open the package to identify the liquid',
          'Ignore it if the load is almost delivered',
        ],
        correctIndex: 1,
        explanation:
          'Drivers do not repair or open leaking packages. The correct response is safe stop, isolation, and escalation.',
      },
      {
        prompt: 'Why does load securement matter even for small packages?',
        options: [
          'It only matters for commercial box trucks',
          'Movement can damage packages and create exposure risk',
          'It makes the route shorter',
          'It replaces the need for shipping papers',
        ],
        correctIndex: 1,
        explanation:
          'Package movement can create damage and release risk. Securement is part of safe transport for any load.',
      },
    ],
  },
  {
    id: 'security-awareness',
    title: 'Security and Account Protection',
    subtitle: 'Suspicious requests, route integrity, identity checks, and app-account security.',
    estimatedMinutes: 12,
    certificateName: 'ChemCab Security Awareness Certificate',
    sections: [
      {
        heading: 'Shipment security',
        bullets: [
          'Be cautious of unauthorized route changes, identity mismatches, and unusual pickup requests.',
          'Drivers should report suspicious behavior, package theft risk, and account compromise quickly.',
        ],
      },
      {
        heading: 'Account safety',
        bullets: [
          'Protect ChemCab account access, avoid sharing credentials, and use approved devices only.',
          'If the app or account seems compromised, stop and escalate.',
        ],
      },
    ],
    questions: [
      {
        prompt: 'What should a driver do if someone requests an unapproved reroute outside normal dispatch channels?',
        options: [
          'Comply immediately if they sound confident',
          'Accept the new route and explain later',
          'Pause and verify through approved ChemCab channels',
          'Share account access so they can update it themselves',
        ],
        correctIndex: 2,
        explanation:
          'Unexpected route changes are a security risk. Drivers should verify through approved dispatch channels before acting.',
      },
      {
        prompt: 'Which credential practice matches ChemCab policy?',
        options: [
          'Share the account if a teammate is busy',
          'Reuse the same weak password everywhere',
          'Protect the account and report suspected compromise immediately',
          'Text login codes to anyone who asks',
        ],
        correctIndex: 2,
        explanation:
          'Account credentials must stay protected. Suspected compromise should be escalated right away.',
      },
      {
        prompt: 'Why do suspicious pickup or identity mismatches matter?',
        options: [
          'They can signal theft, fraud, or unsafe transfer conditions',
          'They only affect payroll',
          'They make the map load slower',
          'They are harmless if the package is small',
        ],
        correctIndex: 0,
        explanation:
          'Identity mismatches can signal fraud, theft, or unsafe release conditions. They must be checked before handoff.',
      },
    ],
  },
];

const NAV_ITEMS = [
  { id: 'overview', label: 'Dashboard' },
  { id: 'training', label: 'Training' },
  { id: 'profile', label: 'Profile' },
  { id: 'certificates', label: 'Certificates' },
];

const STATUS_LABELS = {
  missing: 'Missing',
  submitted: 'Pending review',
  verified: 'Verified',
  rejected: 'Needs update',
  available: 'Ready',
  passed: 'Passed',
};

let placesReadyPromise = null;

function createDriverProfileDocId(email) {
  return String(email || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_');
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const [, base64Data = ''] = result.split(',', 2);
      if (!base64Data) {
        reject(new Error('Could not encode the selected file.'));
        return;
      }
      resolve(base64Data);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read the selected file.'));
    reader.readAsDataURL(file);
  });
}

function getFileExtension(name = '') {
  const trimmed = String(name || '').trim().toLowerCase();
  const parts = trimmed.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

function canPreviewStoredFile(mimeType = '', fileName = '') {
  const normalizedMime = String(mimeType || '').trim().toLowerCase();
  const extension = getFileExtension(fileName);
  return normalizedMime.startsWith('image/')
    || normalizedMime === 'application/pdf'
    || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'pdf'].includes(extension);
}

async function fetchWithDriverAuth(path, options = {}) {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('You need to be signed in to upload onboarding documents.');
  }
  const token = await currentUser.getIdToken();
  const url = `${API_BASE.replace(/\/+$/, '')}/${String(path || '').replace(/^\/+/, '')}`;
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
}

function normalizeModuleDefinition(raw = {}) {
  return {
    id: String(raw.id || '').trim(),
    title: String(raw.title || '').trim(),
    subtitle: String(raw.subtitle || raw.summary || '').trim(),
    estimatedMinutes: Math.max(1, Number(raw.estimatedMinutes) || 10),
    moduleVersion: Math.max(1, Number(raw.moduleVersion) || 1),
    certificateName:
      String(raw.certificateName || '').trim()
      || `${String(raw.title || 'ChemCab Training').trim()} Certificate`,
    summary: String(raw.summary || '').trim(),
    sections: Array.isArray(raw.sections)
      ? raw.sections
          .map((section) => ({
            heading: String(section?.heading || '').trim(),
            body: String(section?.body || '').trim(),
            bullets: Array.isArray(section?.bullets)
              ? section.bullets.map((entry) => String(entry || '').trim()).filter(Boolean)
              : [],
            imageUrl: String(section?.imageUrl || '').trim(),
            imageCaption: String(section?.imageCaption || '').trim(),
            imageAlt: String(section?.imageAlt || '').trim(),
          }))
          .filter((section) => (
            section.heading
            || section.body
            || section.bullets.length
            || section.imageUrl
          ))
      : [],
    questions: Array.isArray(raw.questions)
      ? raw.questions
          .map((question) => ({
            prompt: String(question?.prompt || '').trim(),
            options: Array.isArray(question?.options)
              ? question.options.map((entry) => String(entry || '').trim()).filter(Boolean)
              : [],
            correctIndex: Number.isInteger(Number(question?.correctIndex))
              ? Number(question.correctIndex)
              : 0,
            explanation: String(question?.explanation || '').trim(),
          }))
          .filter((question) => question.prompt && question.options.length >= 2)
      : [],
    active: raw.active !== false,
  };
}

function getAssignedTrainingModules(state, trainingModules) {
  const moduleRows = Array.isArray(trainingModules) ? trainingModules : [];
  const assignedModuleIds = Array.isArray(state?.assignedModuleIds)
    ? state.assignedModuleIds.map((entry) => String(entry || '').trim()).filter(Boolean)
    : [];

  if (!assignedModuleIds.length) {
    return moduleRows.filter((module) => module.active !== false);
  }

  const assignedSet = new Set(assignedModuleIds);
  const filtered = moduleRows.filter((module) => assignedSet.has(module.id));
  return filtered.length ? filtered : moduleRows;
}

function getRequiredDocumentLibrary(state, documentLibrary) {
  const libraryRows = Array.isArray(documentLibrary) ? documentLibrary : [];
  const requiredIds = Array.isArray(state?.requiredDocumentIds)
    ? state.requiredDocumentIds.map((entry) => String(entry || '').trim()).filter(Boolean)
    : [];

  if (!requiredIds.length) return libraryRows;

  const requiredSet = new Set(requiredIds);
  const filtered = libraryRows.filter((entry) => requiredSet.has(entry.id));
  return filtered.length ? filtered : libraryRows;
}

function normalizeDate(value) {
  return String(value || '').slice(0, 10);
}

function normalizeDriverLicenseNumberInput(value) {
  return String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, '')
    .slice(0, 24);
}

function normalizeStateCode(value) {
  return String(value || '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 2);
}

function isValidDriverLicenseNumber(value) {
  return /^[A-Z0-9-]{5,24}$/.test(String(value || '').trim());
}

function isValidStateCode(value) {
  return /^[A-Z]{2}$/.test(String(value || '').trim());
}

function joinAddressLines(line1, line2) {
  const first = String(line1 || '').trim();
  const second = String(line2 || '').trim();
  if (first && second) return `${first}, ${second}`;
  return first || second || '';
}

function ensurePlacesAutocompleteReady() {
  if (placesReadyPromise) return placesReadyPromise;

  placesReadyPromise = new Promise(async (resolve) => {
    const resolveIfReady = () => {
      const g = window.google;
      if (g?.maps?.places?.Autocomplete) {
        resolve(g.maps.places);
        return true;
      }
      return false;
    };

    if (resolveIfReady()) return;

    try {
      const g = window.google;
      if (g?.maps?.importLibrary) {
        await g.maps.importLibrary('places');
        if (resolveIfReady()) return;
      }
    } catch (error) {
      console.warn('[PLACES] importLibrary failed, falling back to script loader:', error);
    }

    if (!GOOGLE_MAPS_API_KEY) {
      resolve(null);
      return;
    }

    const existing = document.getElementById('chemcab-onboarding-places-script');
    if (!existing) {
      const script = document.createElement('script');
      script.id = 'chemcab-onboarding-places-script';
      script.async = true;
      script.defer = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
        GOOGLE_MAPS_API_KEY
      )}&libraries=places&v=weekly&loading=async`;
      document.head.appendChild(script);
    }

    let attempts = 0;
    const maxAttempts = 40;
    const interval = window.setInterval(() => {
      if (resolveIfReady() || attempts++ > maxAttempts) {
        window.clearInterval(interval);
        if (attempts > maxAttempts) resolve(null);
      }
    }, 250);
  });

  return placesReadyPromise;
}

function formatDateTime(value) {
  if (!value) return 'Not yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not yet';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDateShort(value) {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function addYearsToIsoDate(value, years) {
  const date = value ? new Date(value) : new Date();
  date.setFullYear(date.getFullYear() + years);
  return date.toISOString();
}

function makeCertificateId(moduleId, issuedAt) {
  const safeModuleId = String(moduleId || 'module').replace(/[^a-z0-9]/gi, '').toUpperCase();
  const stamp = new Date(issuedAt || Date.now()).toISOString().slice(0, 10).replace(/-/g, '');
  return `CC-${safeModuleId}-${stamp}`;
}

function normalizeStorageKey(email) {
  return `${DASHBOARD_STORAGE_PREFIX}:${String(email || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')}`;
}

function createInitialDocumentState(documentLibrary = DOCUMENT_LIBRARY) {
  const initial = {};
  documentLibrary.forEach((doc) => {
    initial[doc.id] = {
      status: 'missing',
      fileName: '',
      mimeType: '',
      sizeBytes: 0,
      storagePath: '',
      storageBucket: '',
      uploadedAt: '',
      reviewedAt: '',
      uploadedByEmail: '',
      reviewedByEmail: '',
      expiresOn: '',
      note: '',
    };
  });
  return initial;
}

function createInitialModuleState(trainingModules = TRAINING_MODULES) {
  const initial = {};
  trainingModules.forEach((module) => {
    initial[module.id] = {
      status: 'available',
      attempts: 0,
      bestScore: 0,
      lastScore: null,
      passedAt: '',
      certificateId: '',
      expiresOn: '',
      lastAttemptAt: '',
    };
  });
  return initial;
}

function getBootstrapAssignedModuleIds(
  trainingModules = TRAINING_MODULES,
  bootstrapModuleIds = FALLBACK_BOOTSTRAP_MODULE_IDS
) {
  const activeModuleIds = trainingModules
    .filter((entry) => entry.active !== false)
    .map((entry) => String(entry.id || '').trim())
    .filter(Boolean);
  const activeSet = new Set(activeModuleIds);
  const requestedIds = Array.isArray(bootstrapModuleIds) && bootstrapModuleIds.length
    ? bootstrapModuleIds
    : activeModuleIds;
  return Array.from(
    new Set(
      requestedIds
        .map((entry) => String(entry || '').trim())
        .filter((entry) => entry && activeSet.has(entry))
    )
  );
}

function createInitialDashboardState(
  user,
  trainingModules = TRAINING_MODULES,
  documentLibrary = DOCUMENT_LIBRARY,
  bootstrapModuleIds = FALLBACK_BOOTSTRAP_MODULE_IDS
) {
  const email = String(user?.email || '').trim().toLowerCase();
  const requiredDocumentIds = documentLibrary.map((entry) => entry.id);
  const assignedModuleIds = getBootstrapAssignedModuleIds(trainingModules, bootstrapModuleIds);

  return {
    profile: {
      legalName: String(user?.displayName || 'ChemCab Driver').trim(),
      preferredName: String(user?.displayName || 'Driver').trim().split(' ')[0] || 'Driver',
      chemcabEmail: email,
      phone: '',
      teamName: '',
      homeGeofence: '',
      secondaryGeofence: '',
      vehicleSummary: '',
      licenseClass: '',
      endorsements: '',
    },
    driverLink: {
      driverId: '',
      canonicalDriverDocId: '',
      complianceProfileDocId: createDriverProfileDocId(email),
      companyEmail: email,
      personalEmail: '',
      onboardingSubmissionId: '',
      onboardingStatus: '',
      profileSchemaVersion: DRIVER_PROFILE_SCHEMA_VERSION,
    },
    requiredDocumentIds,
    assignedModuleIds,
    documents: createInitialDocumentState(documentLibrary),
    modules: createInitialModuleState(trainingModules),
    history: [],
  };
}

function mergeProfileState(baseProfile = {}, nextProfile = {}) {
  const merged = { ...baseProfile, ...nextProfile };
  Object.keys(merged).forEach((key) => {
    if (merged[key] === undefined || merged[key] === null) {
      delete merged[key];
    }
  });
  return merged;
}

function mergeDriverLinkState(baseLink = {}, nextLink = {}, source = {}, user = null) {
  const fallbackEmail = String(
    user?.email
    || source?.driverEmail
    || source?.companyEmail
    || source?.profile?.chemcabEmail
    || baseLink?.companyEmail
    || ''
  )
    .trim()
    .toLowerCase();

  return {
    driverId: String(nextLink?.driverId || source?.driverId || baseLink?.driverId || '').trim(),
    canonicalDriverDocId: String(
      nextLink?.canonicalDriverDocId
      || source?.canonicalDriverDocId
      || baseLink?.canonicalDriverDocId
      || ''
    ).trim(),
    complianceProfileDocId: String(
      nextLink?.complianceProfileDocId
      || source?.profileDocId
      || source?.complianceProfileDocId
      || baseLink?.complianceProfileDocId
      || createDriverProfileDocId(fallbackEmail)
    ).trim(),
    companyEmail: String(
      nextLink?.companyEmail
      || source?.companyEmail
      || source?.driverEmail
      || baseLink?.companyEmail
      || fallbackEmail
    )
      .trim()
      .toLowerCase(),
    personalEmail: String(
      nextLink?.personalEmail
      || source?.personalEmail
      || baseLink?.personalEmail
      || ''
    )
      .trim()
      .toLowerCase(),
    onboardingSubmissionId: String(
      nextLink?.onboardingSubmissionId
      || source?.onboardingSubmissionId
      || baseLink?.onboardingSubmissionId
      || ''
    ).trim(),
    onboardingStatus: String(
      nextLink?.onboardingStatus
      || source?.onboardingStatus
      || baseLink?.onboardingStatus
      || ''
    ).trim(),
    profileSchemaVersion:
      Number(nextLink?.profileSchemaVersion || source?.profileSchemaVersion || baseLink?.profileSchemaVersion || DRIVER_PROFILE_SCHEMA_VERSION)
      || DRIVER_PROFILE_SCHEMA_VERSION,
  };
}

function mergeDocumentState(primary = {}, secondary = {}) {
  return serverDocumentState(secondary);
}

function mergeModuleState(primary = {}, secondary = {}) {
  const passedPrimary = primary?.status === 'passed';
  const passedSecondary = secondary?.status === 'passed';
  const primaryTimestamp = new Date(primary?.passedAt || primary?.lastAttemptAt || 0).getTime();
  const secondaryTimestamp = new Date(secondary?.passedAt || secondary?.lastAttemptAt || 0).getTime();
  const primaryScore = Math.max(Number(primary?.bestScore || 0), Number(primary?.lastScore || 0));
  const secondaryScore = Math.max(Number(secondary?.bestScore || 0), Number(secondary?.lastScore || 0));
  let source = secondary;

  if (passedPrimary && !passedSecondary) {
    source = primary;
  } else if (passedSecondary && !passedPrimary) {
    source = secondary;
  } else if (Number.isFinite(primaryTimestamp) || Number.isFinite(secondaryTimestamp)) {
    source = secondaryTimestamp >= primaryTimestamp ? secondary : primary;
  } else if (primaryScore > secondaryScore) {
    source = primary;
  }

  return {
    status: source?.status === 'passed' || primary?.status === 'passed' || secondary?.status === 'passed'
      ? 'passed'
      : String(source?.status || primary?.status || secondary?.status || 'available'),
    attempts: Math.max(Number(primary?.attempts || 0), Number(secondary?.attempts || 0)),
    bestScore: Math.max(Number(primary?.bestScore || 0), Number(secondary?.bestScore || 0)),
    lastScore:
      primary?.lastAttemptAt && secondary?.lastAttemptAt
        ? (new Date(primary.lastAttemptAt).getTime() >= new Date(secondary.lastAttemptAt).getTime()
          ? primary.lastScore
          : secondary.lastScore)
        : (primary?.lastScore ?? secondary?.lastScore ?? null),
    passedAt: String(primary?.passedAt || secondary?.passedAt || ''),
    certificateId: String(primary?.certificateId || secondary?.certificateId || ''),
    expiresOn: String(primary?.expiresOn || secondary?.expiresOn || ''),
    lastAttemptAt: String(primary?.lastAttemptAt || secondary?.lastAttemptAt || ''),
  };
}

function normalizeDashboardState(
  rawState,
  user,
  trainingModules = TRAINING_MODULES,
  documentLibrary = DOCUMENT_LIBRARY,
  bootstrapModuleIds = FALLBACK_BOOTSTRAP_MODULE_IDS
) {
  const base = createInitialDashboardState(user, trainingModules, documentLibrary, bootstrapModuleIds);
  const source = rawState && typeof rawState === 'object' ? rawState : {};
  const normalized = {
    profile: mergeProfileState(base.profile, source.profile || {}),
    driverLink: mergeDriverLinkState(base.driverLink, source.driverLink || {}, source, user),
    requiredDocumentIds: Array.from(
      new Set(
        (Array.isArray(source.requiredDocumentIds) ? source.requiredDocumentIds : base.requiredDocumentIds)
          .map((entry) => String(entry || '').trim())
          .filter(Boolean)
      )
    ),
    assignedModuleIds: Array.from(
      new Set(
        [
          ...(base.assignedModuleIds || []),
          ...(Array.isArray(source.assignedModuleIds) ? source.assignedModuleIds : []),
        ]
          .map((entry) => String(entry || '').trim())
          .filter(Boolean)
      )
    ),
    documents: {},
    modules: {},
    history: Array.isArray(source.history)
      ? source.history
          .map((entry) => ({
            id: String(entry?.id || '').trim(),
            type: String(entry?.type || '').trim(),
            title: String(entry?.title || '').trim(),
            detail: String(entry?.detail || '').trim(),
            at: String(entry?.at || '').trim(),
          }))
          .filter((entry) => entry.id && entry.title)
          .slice(0, 60)
      : base.history,
  };

  documentLibrary.forEach((entry) => {
    normalized.documents[entry.id] = mergeDocumentState(
      base.documents?.[entry.id] || {},
      source?.documents?.[entry.id] || {}
    );
  });

  trainingModules.forEach((module) => {
    normalized.modules[module.id] = mergeModuleState(
      base.modules?.[module.id] || {},
      source?.modules?.[module.id] || {}
    );
  });

  return normalized;
}

function reconcileDashboardStates(
  remoteState,
  localState,
  user,
  trainingModules = TRAINING_MODULES,
  documentLibrary = DOCUMENT_LIBRARY,
  bootstrapModuleIds = FALLBACK_BOOTSTRAP_MODULE_IDS
) {
  const remote = remoteState
    ? normalizeDashboardState(remoteState, user, trainingModules, documentLibrary, bootstrapModuleIds)
    : null;
  const local = localState
    ? normalizeDashboardState(localState, user, trainingModules, documentLibrary, bootstrapModuleIds)
    : null;

  if (!remote && !local) return createInitialDashboardState(user, trainingModules, documentLibrary, bootstrapModuleIds);
  if (!remote) return local;
  if (!local) return remote;

  const merged = {
    profile: mergeProfileState(remote.profile, local.profile),
    driverLink: mergeDriverLinkState(remote.driverLink, local.driverLink, remoteState || {}, user),
    requiredDocumentIds: Array.from(new Set([...(remote.requiredDocumentIds || []), ...(local.requiredDocumentIds || [])])),
    assignedModuleIds: Array.from(new Set([...(remote.assignedModuleIds || []), ...(local.assignedModuleIds || [])])),
    documents: {},
    modules: {},
    history: [],
  };

  documentLibrary.forEach((entry) => {
    merged.documents[entry.id] = mergeDocumentState(local.documents?.[entry.id], remote.documents?.[entry.id]);
  });

  trainingModules.forEach((module) => {
    merged.modules[module.id] = mergeModuleState(local.modules?.[module.id], remote.modules?.[module.id]);
  });

  const historyMap = new Map();
  [...(remote.history || []), ...(local.history || [])]
    .filter((entry) => entry?.id)
    .forEach((entry) => {
      historyMap.set(entry.id, entry);
    });
  merged.history = Array.from(historyMap.values())
    .sort((a, b) => new Date(b.at || 0).getTime() - new Date(a.at || 0).getTime())
    .slice(0, 60);

  return merged;
}

function readDashboardState(email) {
  if (!email) return null;
  try {
    const raw = window.localStorage.getItem(normalizeStorageKey(email));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_error) {
    return null;
  }
}

function writeDashboardState(email, value) {
  if (!email) return;
  try {
    window.localStorage.setItem(normalizeStorageKey(email), JSON.stringify(value));
  } catch (_error) {
    // Ignore local storage failures.
  }
}

function appendHistoryEntry(state, entry) {
  const history = Array.isArray(state.history) ? state.history : [];
  return {
    ...state,
    history: [entry, ...history].slice(0, 60),
  };
}

function getDocumentMeta(documentId, documentLibrary = DOCUMENT_LIBRARY) {
  return documentLibrary.find((entry) => entry.id === documentId) || null;
}

function isExpiredDate(value) {
  if (!value) return false;
  const date = new Date(`${value}T23:59:59`);
  return !Number.isNaN(date.getTime()) && date.getTime() < Date.now();
}

function isExpiringSoon(value, days = 21) {
  if (!value) return false;
  const date = new Date(`${value}T23:59:59`);
  if (Number.isNaN(date.getTime())) return false;
  const diff = date.getTime() - Date.now();
  return diff > 0 && diff <= days * 24 * 60 * 60 * 1000;
}

function buildComplianceSummary(
  state,
  trainingModules = TRAINING_MODULES,
  documentLibrary = DOCUMENT_LIBRARY
) {
  const modules = state.modules || {};
  const documents = state.documents || {};
  const activeTrainingModules = getAssignedTrainingModules(state, trainingModules);
  const requiredDocumentLibrary = getRequiredDocumentLibrary(state, documentLibrary);

  const totalModuleCount = activeTrainingModules.length;
  const passedModules = activeTrainingModules.filter(
    (module) => modules[module.id]?.status === 'passed'
  ).length;

  const requiredDocuments = requiredDocumentLibrary.map((entry) => entry.id);
  const verifiedDocuments = requiredDocuments.filter((documentId) => {
    const doc = documents[documentId];
    return doc?.status === 'verified' && !isExpiredDate(doc?.expiresOn);
  }).length;
  const submittedDocuments = requiredDocuments.filter(
    (documentId) => documents[documentId]?.status === 'submitted'
  ).length;

  const totalChecklistItems = totalModuleCount + requiredDocuments.length;
  const completedChecklistItems = passedModules + verifiedDocuments;
  const weightedCompletion =
    totalChecklistItems === 0
      ? 0
      : Math.round(
          ((completedChecklistItems + submittedDocuments * 0.5) / totalChecklistItems) * 100
        );

  const tasks = [];
  requiredDocuments.forEach((documentId) => {
    const meta = getDocumentMeta(documentId, requiredDocumentLibrary);
    const doc = documents[documentId] || {};
    if (doc.status === 'missing') {
      tasks.push(`Upload ${meta?.title || documentId}.`);
    } else if (doc.status === 'submitted') {
      tasks.push(`${meta?.title || documentId} is waiting for compliance review.`);
    } else if (doc.status === 'rejected') {
      tasks.push(`Replace ${meta?.title || documentId}; the previous upload needs attention.`);
    } else if (doc.status === 'verified' && isExpiredDate(doc.expiresOn)) {
      tasks.push(`${meta?.title || documentId} expired and must be replaced.`);
    } else if (doc.status === 'verified' && isExpiringSoon(doc.expiresOn)) {
      tasks.push(`${meta?.title || documentId} expires soon. Refresh it before it blocks dispatch.`);
    }
  });

  activeTrainingModules.forEach((module) => {
    if (modules[module.id]?.status !== 'passed') {
      tasks.push(`Pass ${module.title}.`);
    }
  });

  const activeCertificates = activeTrainingModules.filter(
    (module) => modules[module.id]?.status === 'passed'
  ).map((module) => ({
    moduleId: module.id,
    title: module.certificateName,
    issuedAt: modules[module.id]?.passedAt || '',
    expiresOn: modules[module.id]?.expiresOn || '',
    certificateId: modules[module.id]?.certificateId || '',
  }));

  const blocked = tasks.filter(
    (task) =>
      task.includes('Upload') ||
      task.includes('expired') ||
      task.includes('Pass') ||
      task.includes('needs attention')
  );
  const warning = tasks.filter((task) => task.includes('waiting') || task.includes('expires soon'));

  let status = 'green';
  if (blocked.length > 0) status = 'red';
  else if (warning.length > 0) status = 'yellow';

  return {
    passedModules,
    verifiedDocuments,
    submittedDocuments,
    totalChecklistItems,
    completedChecklistItems,
    weightedCompletion,
    activeCertificates,
    tasks,
    blocked,
    warning,
    status,
    onlineEligible: blocked.length === 0,
  };
}

function buildDriverProfilePatchFromDashboardState(normalizedState, user) {
  const companyEmail = String(
    normalizedState?.driverLink?.companyEmail
    || normalizedState?.profile?.chemcabEmail
    || user?.email
    || ''
  )
    .trim()
    .toLowerCase();

  return {
    schemaVersion: DRIVER_PROFILE_SCHEMA_VERSION,
    driverId: String(normalizedState?.driverLink?.driverId || '').trim(),
    legalName: String(normalizedState?.profile?.legalName || user?.displayName || '').trim(),
    preferredName: String(normalizedState?.profile?.preferredName || '').trim(),
    companyEmail,
    contact: {
      companyEmail,
      personalEmail: String(normalizedState?.driverLink?.personalEmail || '').trim().toLowerCase(),
      phone: String(normalizedState?.profile?.phone || '').trim(),
    },
    team: {
      teamName: String(normalizedState?.profile?.teamName || '').trim(),
      primaryGeofence: String(normalizedState?.profile?.homeGeofence || '').trim(),
      secondaryGeofence: String(normalizedState?.profile?.secondaryGeofence || '').trim(),
    },
    vehicle: {
      summary: String(normalizedState?.profile?.vehicleSummary || '').trim(),
    },
    driverLicense: {
      class: String(normalizedState?.profile?.licenseClass || '').trim(),
      endorsements: String(normalizedState?.profile?.endorsements || '').trim(),
    },
    onboarding: {
      submissionId: String(normalizedState?.driverLink?.onboardingSubmissionId || '').trim(),
      lastUpdatedFromDashboardAt: new Date().toISOString(),
      source: 'driver_dashboard',
    },
  };
}

async function persistDashboardStateToCloud({
  state,
  user,
  trainingModules = TRAINING_MODULES,
  documentLibrary = DOCUMENT_LIBRARY,
  bootstrapModuleIds = FALLBACK_BOOTSTRAP_MODULE_IDS,
}) {
  if (!user?.email) return;
  const response = await fetchWithDriverAuth('/api/driver/onboarding/workspace', {
    method: 'PATCH',
    body: JSON.stringify({
      state: serializeDashboardStateForFirestore(
        state,
        user,
        trainingModules,
        documentLibrary,
        bootstrapModuleIds
      ),
    }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body?.message || body?.error || `Could not save onboarding workspace (${response.status}).`);
  }
}

function serializeDashboardStateForFirestore(
  state,
  user,
  trainingModules = TRAINING_MODULES,
  documentLibrary = DOCUMENT_LIBRARY,
  bootstrapModuleIds = FALLBACK_BOOTSTRAP_MODULE_IDS
) {
  const normalized = normalizeDashboardState(state, user, trainingModules, documentLibrary, bootstrapModuleIds);
  const summary = buildComplianceSummary(normalized, trainingModules, documentLibrary);
  const activeTrainingModules = getAssignedTrainingModules(normalized, trainingModules);
  const requiredDocumentLibrary = getRequiredDocumentLibrary(normalized, documentLibrary);
  const overdueDocumentIds = requiredDocumentLibrary
    .filter((entry) => {
      const docState = normalized.documents?.[entry.id] || {};
      return docState.status === 'verified' && isExpiredDate(docState.expiresOn);
    })
    .map((entry) => entry.id);
  const expiringSoonDocumentIds = requiredDocumentLibrary
    .filter((entry) => {
      const docState = normalized.documents?.[entry.id] || {};
      return docState.status === 'verified' && isExpiringSoon(docState.expiresOn);
    })
    .map((entry) => entry.id);
  const completedModuleIds = activeTrainingModules
    .filter((module) => normalized.modules?.[module.id]?.status === 'passed')
    .map((module) => module.id);
  const pendingModuleIds = activeTrainingModules
    .filter((module) => normalized.modules?.[module.id]?.status !== 'passed')
    .map((module) => module.id);
  const missingDocumentIds = requiredDocumentLibrary
    .filter((entry) => {
      const docState = normalized.documents?.[entry.id] || {};
      return docState.status !== 'verified' || isExpiredDate(docState.expiresOn);
    })
    .map((entry) => entry.id);

  return {
    ...normalized,
    driverEmail: String(user?.email || normalized.profile?.chemcabEmail || '').trim().toLowerCase(),
    driverId: String(normalized?.driverLink?.driverId || '').trim() || null,
    canonicalDriverDocId: String(normalized?.driverLink?.canonicalDriverDocId || '').trim() || null,
    profileDocId:
      String(normalized?.driverLink?.complianceProfileDocId || '').trim()
      || createDriverProfileDocId(String(user?.email || normalized.profile?.chemcabEmail || '').trim().toLowerCase()),
    companyEmail:
      String(normalized?.driverLink?.companyEmail || user?.email || normalized.profile?.chemcabEmail || '')
        .trim()
        .toLowerCase() || null,
    personalEmail:
      String(normalized?.driverLink?.personalEmail || '').trim().toLowerCase() || null,
    onboardingSubmissionId:
      String(normalized?.driverLink?.onboardingSubmissionId || '').trim() || null,
    onboardingStatus:
      String(normalized?.driverLink?.onboardingStatus || '').trim()
      || 'provisioned',
    profileSchemaVersion: DRIVER_PROFILE_SCHEMA_VERSION,
    driverProfile: buildDriverProfilePatchFromDashboardState(normalized, user),
    driverName: String(normalized.profile?.legalName || user?.displayName || '').trim(),
    teamName: String(normalized.profile?.teamName || '').trim(),
    primaryGeofence: String(normalized.profile?.homeGeofence || '').trim(),
    secondaryGeofence: String(normalized.profile?.secondaryGeofence || '').trim(),
    onlineEligible: summary.onlineEligible,
    progressPercent: summary.weightedCompletion,
    status: summary.onlineEligible ? (summary.warning.length ? 'warning' : 'compliant') : 'blocked',
    overdueCount: overdueDocumentIds.length,
    expiringSoonCount: expiringSoonDocumentIds.length,
    requiredDocumentIds: requiredDocumentLibrary.map((entry) => entry.id),
    missingDocumentIds,
    assignedModuleIds: activeTrainingModules.map((module) => module.id),
    pendingModuleIds,
    completedModuleIds,
    activeCertificateIds: summary.activeCertificates.map((entry) => entry.certificateId).filter(Boolean),
    complianceTasks: summary.tasks,
    source: 'driver-dashboard',
    updatedAt: new Date().toISOString(),
    lastSyncedAt: new Date().toISOString(),
  };
}

function StatusPill({ tone, children }) {
  return <span className={`status-pill status-pill-${tone}`}>{children}</span>;
}

function MetricCard({ label, value, note }) {
  return (
    <article className="metric-card">
      <span className="metric-label">{label}</span>
      <strong className="metric-value">{value}</strong>
      {note ? <p className="metric-note">{note}</p> : null}
    </article>
  );
}

function ProgressBar({ value }) {
  return (
    <div className="progress-rail" aria-hidden="true">
      <div className="progress-fill" style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}

function TaskBanner({ summary }) {
  if (!summary.tasks.length) {
    return (
      <section className="todo-banner todo-banner-green">
        <div>
          <p className="todo-kicker">Ready for dispatch</p>
          <h3>All required items are complete right now.</h3>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`todo-banner ${
        summary.status === 'red'
          ? 'todo-banner-red'
          : summary.status === 'yellow'
            ? 'todo-banner-yellow'
            : 'todo-banner-green'
      }`}
    >
      <div>
        <p className="todo-kicker">Things to do</p>
        <h3>
          {summary.blocked.length > 0
            ? 'You still have blocked items before you are fully compliant.'
            : 'You are close. Finish these items to stay compliant.'}
        </h3>
      </div>
      <ul className="todo-list">
        {summary.tasks.slice(0, 5).map((task) => (
          <li key={task}>{task}</li>
        ))}
      </ul>
    </section>
  );
}

function RecentHistory({ history }) {
  if (!history.length) {
    return (
      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">History</p>
            <h3>Nothing logged yet</h3>
          </div>
        </div>
        <p className="muted-copy">
          Module attempts, document uploads, and certificate events will appear here.
        </p>
      </section>
    );
  }

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <p className="panel-kicker">History</p>
          <h3>Recent activity</h3>
        </div>
      </div>
      <div className="history-list">
        {history.slice(0, 8).map((entry) => (
          <article className="history-item" key={entry.id}>
            <div className="history-dot" />
            <div>
              <strong>{entry.title}</strong>
              <p>{entry.detail}</p>
              <span>{formatDateTime(entry.at)}</span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function OverviewPage({
  user,
  state,
  summary,
  onNavigate,
  trainingModules = TRAINING_MODULES,
  documentLibrary = DOCUMENT_LIBRARY,
}) {
  const nextCertificate = summary.activeCertificates[0] || null;

  return (
    <div className="page-stack">
      <section className="hero-card">
        <div className="hero-copy">
          <p className="hero-kicker">Driver compliance dashboard</p>
          <h1>
            {String(user?.displayName || '').trim() || 'Driver'}, here is how close you are to being
            fully compliant.
          </h1>
          <p className="hero-subcopy">
            Finish modules, keep documents current, and use your ChemCab Google account to track
            readiness from one place.
          </p>
          <div className="hero-meta">
            <StatusPill tone={summary.status}>
              {summary.onlineEligible ? 'Eligible to go online' : 'Online access blocked'}
            </StatusPill>
            <span>{summary.completedChecklistItems} completed checkpoints</span>
          </div>
        </div>
        <div className="hero-progress-card">
          <div className="radial-progress" style={{ '--progress': `${summary.weightedCompletion}%` }}>
            <div className="radial-progress-inner">
              <strong>{summary.weightedCompletion}%</strong>
              <span>Compliance progress</span>
            </div>
          </div>
          <ProgressBar value={summary.weightedCompletion} />
          <p className="hero-progress-note">
            {summary.onlineEligible
              ? 'Everything required for online eligibility is in place.'
              : `${summary.blocked.length} blocking item${summary.blocked.length === 1 ? '' : 's'} still need attention.`}
          </p>
        </div>
      </section>

      <TaskBanner summary={summary} />

      <section className="metric-grid">
        <MetricCard
          label="Modules passed"
          value={`${summary.passedModules}/${trainingModules.length}`}
          note="100% score required on every exam."
        />
        <MetricCard
          label="Documents verified"
          value={`${summary.verifiedDocuments}/${documentLibrary.length}`}
          note={`${summary.submittedDocuments} document${summary.submittedDocuments === 1 ? '' : 's'} waiting for review.`}
        />
        <MetricCard
          label="Active certificates"
          value={summary.activeCertificates.length}
          note="Certificates unlock as modules are passed."
        />
        <MetricCard
          label="Current status"
          value={summary.onlineEligible ? 'Ready' : 'Blocked'}
          note={
            summary.status === 'yellow'
              ? 'Expiring soon items need attention.'
              : 'Drivers can only go online when required items are complete.'
          }
        />
      </section>

      <section className="two-column-grid">
        <section className="panel">
          <div className="panel-header">
            <div>
              <p className="panel-kicker">Next up</p>
              <h3>Top actions</h3>
            </div>
            <button className="text-button" type="button" onClick={() => onNavigate('training')}>
              Open training
            </button>
          </div>
          <div className="action-list">
            {summary.tasks.slice(0, 4).map((task) => (
              <article className="action-item" key={task}>
                <div className="action-marker" />
                <div>
                  <strong>{task}</strong>
                  <p>
                    {task.includes('Pass')
                      ? 'Module completion updates your certificate status immediately.'
                      : 'Document review status becomes visible here as soon as compliance approves it.'}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <p className="panel-kicker">Current certificate</p>
              <h3>{nextCertificate ? nextCertificate.title : 'No certificate issued yet'}</h3>
            </div>
            <button className="text-button" type="button" onClick={() => onNavigate('certificates')}>
              View all
            </button>
          </div>
          {nextCertificate ? (
            <div className="certificate-highlight">
              <div className="certificate-badge">Active</div>
              <strong>{nextCertificate.certificateId}</strong>
              <p>Issued {formatDateShort(nextCertificate.issuedAt)}</p>
              <p>Refresh due {formatDateShort(nextCertificate.expiresOn)}</p>
            </div>
          ) : (
            <p className="muted-copy">
              Pass your first module to generate the first driver training certificate.
            </p>
          )}
        </section>
      </section>

      <RecentHistory history={Array.isArray(state.history) ? state.history : []} />
    </div>
  );
}

function ModuleCard({ module, moduleState, onSelect, active }) {
  const status = moduleState?.status || 'available';
  const statusTone = status === 'passed' ? 'green' : 'blue';

  return (
    <button
      className={`module-card ${active ? 'module-card-active' : ''}`}
      type="button"
      onClick={onSelect}
    >
      <div className="module-card-top">
        <div>
          <p className="module-kicker">{module.estimatedMinutes} min</p>
          <h3>{module.title}</h3>
        </div>
        <StatusPill tone={statusTone}>{STATUS_LABELS[status] || status}</StatusPill>
      </div>
      <p className="module-subtitle">{module.subtitle}</p>
      <div className="module-card-foot">
        <span>{module.questions.length} questions</span>
        <span>{moduleState?.bestScore ? `${moduleState.bestScore}% best` : '100% required'}</span>
      </div>
    </button>
  );
}

function ModuleSectionCard({ section, compact = false }) {
  return (
    <article className={compact ? 'reference-card' : 'module-summary-card'}>
      {section.heading ? <strong>{section.heading}</strong> : null}
      {section.body ? <p className="module-copy">{section.body}</p> : null}
      {section.imageUrl ? (
        <figure className={compact ? 'reference-card-figure' : 'module-image-frame'}>
          <img
            className={compact ? 'reference-card-image' : 'module-image'}
            src={section.imageUrl}
            alt={section.imageAlt || section.imageCaption || section.heading || 'Training reference'}
            loading="lazy"
          />
          {section.imageCaption ? <figcaption>{section.imageCaption}</figcaption> : null}
        </figure>
      ) : null}
      {section.bullets.length ? (
        <ul>
          {section.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

function ExamWorkspace({
  module,
  examState,
  onSelectAnswer,
  onSubmit,
  onClose,
  onRetake,
}) {
  const result = examState.result;
  const answeredCount = Object.keys(examState.answers || {}).length;

  return (
    <div className="exam-shell">
      <aside className="exam-sidebar">
        <div className="exam-sidebar-panel">
          <p className="panel-kicker">Reference guide</p>
          <h3>{module.title}</h3>
          <p className="muted-copy">
            Use the module notes while testing. Every question must be correct to pass.
          </p>
          <div className="reference-stack">
            {module.sections.map((section, index) => (
              <ModuleSectionCard
                key={`${section.heading || 'section'}-${index}`}
                section={section}
                compact
              />
            ))}
          </div>
        </div>
      </aside>

      <section className="exam-main">
        <div className="exam-header">
          <div>
            <p className="panel-kicker">Exam mode</p>
            <h3>{module.title} assessment</h3>
          </div>
          <button className="ghost-button" type="button" onClick={onClose}>
            Back to module
          </button>
        </div>

        <div className="exam-progress-row">
          <span>
            {answeredCount}/{module.questions.length} answered
          </span>
          <span>Passing score: 100%</span>
        </div>
        <ProgressBar value={(answeredCount / module.questions.length) * 100} />

        <div className="question-stack">
          {module.questions.map((question, questionIndex) => {
            const chosenIndex = examState.answers?.[questionIndex];
            const incorrectEntry = result?.incorrectByQuestion?.[questionIndex] || null;
            const isCorrect = result?.submitted && question.correctIndex === chosenIndex;

            return (
              <article className="question-card" key={question.prompt}>
                <div className="question-card-head">
                  <span className="question-index">Question {questionIndex + 1}</span>
                  {result?.submitted ? (
                    <StatusPill tone={isCorrect ? 'green' : 'red'}>
                      {isCorrect ? 'Correct' : 'Review'}
                    </StatusPill>
                  ) : null}
                </div>
                <h4>{question.prompt}</h4>
                <div className="option-stack">
                  {question.options.map((option, optionIndex) => {
                    let optionClass = 'option-button';
                    if (result?.submitted && optionIndex === question.correctIndex) {
                      optionClass += ' option-button-correct';
                    } else if (
                      result?.submitted &&
                      chosenIndex === optionIndex &&
                      optionIndex !== question.correctIndex
                    ) {
                      optionClass += ' option-button-incorrect';
                    } else if (chosenIndex === optionIndex) {
                      optionClass += ' option-button-selected';
                    }

                    return (
                      <button
                        className={optionClass}
                        type="button"
                        key={option}
                        disabled={Boolean(result?.submitted)}
                        onClick={() => onSelectAnswer(questionIndex, optionIndex)}
                      >
                        <span className="option-letter">
                          {String.fromCharCode(65 + optionIndex)}
                        </span>
                        <span>{option}</span>
                      </button>
                    );
                  })}
                </div>
                {incorrectEntry ? (
                  <div className="answer-explainer">
                    <strong>Why that was incorrect</strong>
                    <p>{incorrectEntry.explanation}</p>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>

        <div className="exam-footer">
          {!result?.submitted ? (
            <button
              className="primary-button"
              type="button"
              disabled={answeredCount !== module.questions.length}
              onClick={onSubmit}
            >
              Submit exam
            </button>
          ) : result.passed ? (
            <div className="exam-result exam-result-pass">
              <div>
                <strong>Passed with 100%</strong>
                <p>Your certificate is now active on the certificates page.</p>
              </div>
              <button className="primary-button" type="button" onClick={onClose}>
                Done
              </button>
            </div>
          ) : (
            <div className="exam-result exam-result-fail">
              <div>
                <strong>{result.score}% scored</strong>
                <p>
                  ChemCab requires 100% before this module counts as complete. Review the
                  explanations, revisit the module notes, and try again.
                </p>
              </div>
              <button className="primary-button" type="button" onClick={onRetake}>
                Retake exam
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function TrainingPage({ state, trainingModules = TRAINING_MODULES, onModuleStateChange }) {
  const [selectedModuleId, setSelectedModuleId] = useState(trainingModules[0]?.id || '');
  const [examState, setExamState] = useState({
    moduleId: '',
    answers: {},
    result: null,
  });

  useEffect(() => {
    if (!trainingModules.length) {
      setSelectedModuleId('');
      return;
    }
    if (!trainingModules.some((module) => module.id === selectedModuleId)) {
      setSelectedModuleId(trainingModules[0].id);
    }
  }, [selectedModuleId, trainingModules]);

  const selectedModule = useMemo(
    () => trainingModules.find((module) => module.id === selectedModuleId) || trainingModules[0] || null,
    [selectedModuleId, trainingModules]
  );

  if (!selectedModule) {
    return (
      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Training</p>
            <h3>No modules assigned yet</h3>
          </div>
        </div>
        <p className="muted-copy">
          Your required training modules will appear here after compliance provisioning finishes.
        </p>
      </section>
    );
  }

  const selectedModuleState = state.modules?.[selectedModule.id] || {};

  const startExam = () => {
    setExamState({
      moduleId: selectedModule.id,
      answers: {},
      result: null,
    });
  };

  const submitExam = () => {
    const incorrect = {};
    selectedModule.questions.forEach((question, questionIndex) => {
      const chosenIndex = examState.answers[questionIndex];
      if (chosenIndex !== question.correctIndex) {
        incorrect[questionIndex] = {
          chosenIndex,
          correctIndex: question.correctIndex,
          explanation: question.explanation,
        };
      }
    });

    const total = selectedModule.questions.length;
    const correct = total - Object.keys(incorrect).length;
    const score = Math.round((correct / total) * 100);
    const passed = correct === total;
    const attemptedAt = new Date().toISOString();

    onModuleStateChange(selectedModule, {
      attemptedAt,
      score,
      passed,
    });

    setExamState((current) => ({
      ...current,
      result: {
        submitted: true,
        passed,
        score,
        attemptedAt,
        incorrectByQuestion: incorrect,
      },
    }));
  };

  const retakeExam = () => {
    setExamState({
      moduleId: selectedModule.id,
      answers: {},
      result: null,
    });
  };

  const closeExam = () => {
    setExamState({
      moduleId: '',
      answers: {},
      result: null,
    });
  };

  const examOpen = examState.moduleId === selectedModule.id;

  return (
    <div className="training-layout">
      <aside className="training-sidebar">
        <section className="panel">
          <div className="panel-header">
            <div>
              <p className="panel-kicker">Training map</p>
              <h3>Assigned modules</h3>
            </div>
          </div>
          <div className="module-list">
            {trainingModules.map((module) => (
              <ModuleCard
                key={module.id}
                module={module}
                moduleState={state.modules?.[module.id]}
                active={selectedModule.id === module.id}
                onSelect={() => {
                  setSelectedModuleId(module.id);
                  setExamState({
                    moduleId: '',
                    answers: {},
                    result: null,
                  });
                }}
              />
            ))}
          </div>
        </section>
      </aside>

      <section className="training-main">
        {!examOpen ? (
          <div className="page-stack">
            <section className="panel module-detail-panel">
              <div className="module-detail-head">
                <div>
                  <p className="panel-kicker">Module detail</p>
                  <h2>{selectedModule.title}</h2>
                  <p className="muted-copy">{selectedModule.subtitle}</p>
                  {selectedModule.summary ? (
                    <p className="module-detail-summary">{selectedModule.summary}</p>
                  ) : null}
                </div>
                <div className="module-detail-actions">
                  <StatusPill tone={selectedModuleState.status === 'passed' ? 'green' : 'blue'}>
                    {STATUS_LABELS[selectedModuleState.status] || 'Ready'}
                  </StatusPill>
                  <span className="module-duration">
                    {selectedModule.estimatedMinutes} min | v{selectedModule.moduleVersion || 1}
                  </span>
                </div>
              </div>

              <div className="module-summary-grid">
                {selectedModule.sections.map((section, index) => (
                  <ModuleSectionCard
                    key={`${section.heading || 'section'}-${index}`}
                    section={section}
                  />
                ))}
              </div>

              <div className="module-footer">
                <div>
                  <strong>Exam format</strong>
                  <p>
                    {selectedModule.questions.length} multiple-choice questions. Drivers must score
                    100% to pass. Incorrect answers return explanations so the next attempt is clear.
                  </p>
                </div>
                <button className="primary-button" type="button" onClick={startExam}>
                  {selectedModuleState.status === 'passed' ? 'Review exam again' : 'Start exam'}
                </button>
              </div>
            </section>

            <section className="panel">
              <div className="panel-header">
                <div>
                  <p className="panel-kicker">Current module status</p>
                  <h3>
                    {selectedModuleState.status === 'passed'
                      ? 'Completed'
                      : selectedModuleState.lastScore
                        ? 'Needs another attempt'
                        : 'Not passed yet'}
                  </h3>
                </div>
              </div>
              <div className="detail-meta-grid">
                <MetricCard
                  label="Best score"
                  value={selectedModuleState.bestScore || 0}
                  note="100 is required"
                />
                <MetricCard
                  label="Attempts"
                  value={selectedModuleState.attempts || 0}
                  note="Retakes are unlimited"
                />
                <MetricCard
                  label="Issued"
                  value={formatDateShort(selectedModuleState.passedAt)}
                  note="Shown when passed"
                />
                <MetricCard
                  label="Refresh due"
                  value={formatDateShort(selectedModuleState.expiresOn)}
                  note="Three-year placeholder cycle"
                />
              </div>
            </section>
          </div>
        ) : (
          <ExamWorkspace
            module={selectedModule}
            examState={examState}
            onSelectAnswer={(questionIndex, optionIndex) => {
              setExamState((current) => ({
                ...current,
                answers: {
                  ...current.answers,
                  [questionIndex]: optionIndex,
                },
              }));
            }}
            onSubmit={submitExam}
            onClose={closeExam}
            onRetake={retakeExam}
          />
        )}
      </section>
    </div>
  );
}

function ProfilePage({
  state,
  summary,
  documentLibrary = DOCUMENT_LIBRARY,
  onProfileChange,
  onDocumentUpload,
  onDocumentFieldChange,
  onDocumentPreview,
  onDocumentRemove,
  documentUploadBusyId = '',
  documentPreviewBusyId = '',
  documentRemovingId = '',
  lockOperationalFields = false,
}) {
  return (
    <div className="page-stack">
      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Driver profile</p>
            <h3>Profile and operational info</h3>
          </div>
          <StatusPill tone={summary.status}>
            {summary.onlineEligible ? 'Ready for online status' : 'Compliance follow-up needed'}
          </StatusPill>
        </div>

        <div className="profile-grid">
          <label className="field">
            <span>Legal name</span>
            <input
              value={state.profile.legalName}
              onChange={(event) => onProfileChange('legalName', event.target.value)}
            />
          </label>
          <label className="field">
            <span>Preferred name</span>
            <input
              value={state.profile.preferredName}
              onChange={(event) => onProfileChange('preferredName', event.target.value)}
            />
          </label>
          <label className="field">
            <span>ChemCab email</span>
            <input value={state.profile.chemcabEmail} readOnly className="field-readonly" />
          </label>
          <label className="field">
            <span>Phone</span>
            <input
              value={state.profile.phone}
              onChange={(event) => onProfileChange('phone', event.target.value)}
            />
          </label>
          <label className="field">
            <span>Team</span>
            <input
              value={state.profile.teamName}
              readOnly={lockOperationalFields}
              className={lockOperationalFields ? 'field-readonly' : ''}
              onChange={(event) => onProfileChange('teamName', event.target.value)}
            />
          </label>
          <label className="field">
            <span>Home geofence</span>
            <input
              value={state.profile.homeGeofence}
              readOnly={lockOperationalFields}
              className={lockOperationalFields ? 'field-readonly' : ''}
              onChange={(event) => onProfileChange('homeGeofence', event.target.value)}
            />
          </label>
          <label className="field field-span-2">
            <span>Vehicle summary</span>
            <input
              value={state.profile.vehicleSummary}
              onChange={(event) => onProfileChange('vehicleSummary', event.target.value)}
            />
          </label>
          <label className="field">
            <span>License class</span>
            <input
              value={state.profile.licenseClass}
              onChange={(event) => onProfileChange('licenseClass', event.target.value)}
            />
          </label>
          <label className="field">
            <span>Endorsements</span>
            <input
              value={state.profile.endorsements}
              onChange={(event) => onProfileChange('endorsements', event.target.value)}
            />
          </label>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Documents</p>
            <h3>Required uploads and review status</h3>
          </div>
        </div>

        <div className="document-grid">
          {documentLibrary.map((documentItem) => {
            const documentState = state.documents?.[documentItem.id] || {};
            const tone =
              documentState.status === 'verified'
                ? 'green'
                : documentState.status === 'submitted'
                  ? 'yellow'
                  : documentState.status === 'rejected'
                    ? 'red'
                    : 'blue';

            return (
              <article className="document-card" key={documentItem.id}>
                <div className="document-card-head">
                  <div>
                    <h4>{documentItem.title}</h4>
                    <p>{documentItem.description}</p>
                  </div>
                  <StatusPill tone={tone}>
                    {STATUS_LABELS[documentState.status] || documentState.status || 'Missing'}
                  </StatusPill>
                </div>

                <div className="document-meta">
                  <div>
                    <span className="meta-label">Current file</span>
                    <strong>{documentState.fileName || 'Nothing uploaded yet'}</strong>
                  </div>
                  <div>
                    <span className="meta-label">Expires</span>
                    <strong>{documentState.expiresOn || 'Add date'}</strong>
                  </div>
                </div>

                <dl className="document-meta" aria-label="Document audit timestamps">
                  <div><dt>Uploaded</dt><dd>{formatEvidenceTime(documentState.uploadedAt)}</dd></div>
                  <div><dt>Reviewed</dt><dd>{formatEvidenceTime(documentState.reviewedAt)}</dd></div>
                  <div><dt>Review invalidated</dt><dd>{formatEvidenceTime(documentState.verificationInvalidatedAt)}</dd></div>
                </dl>
                {documentState.status === 'verified' && !documentState.reviewedAt && (
                  <p className="field-hint">Verification status is recorded, but the original review time is not available.</p>
                )}

                <div className="document-controls">
                  <label className="field">
                    <span>Upload or replace</span>
                    <input
                      type="file"
                      accept={documentItem.accept}
                      disabled={documentUploadBusyId === documentItem.id}
                      onChange={(event) =>
                        onDocumentUpload(documentItem.id, event.target.files?.[0] || null)
                      }
                    />
                    <span className="field-hint">
                      Accepts PDF, JPEG, PNG, and DOCX up to {MAX_UPLOAD_MB} MB.
                    </span>
                  </label>
                  <label className="field">
                    <span>Expiration date</span>
                    <input
                      type="date"
                      value={documentState.expiresOn || ''}
                      onChange={(event) =>
                        onDocumentFieldChange(documentItem.id, 'expiresOn', event.target.value)
                      }
                    />
                  </label>
                </div>

                <div className="document-actions">
                  <button
                    type="button"
                    className="ghost-button"
                    onClick={() => onDocumentPreview(documentItem.id)}
                    disabled={
                      !documentState.storagePath
                      || !canPreviewStoredFile(documentState.mimeType, documentState.fileName)
                      || documentPreviewBusyId === documentItem.id
                    }
                  >
                    {documentPreviewBusyId === documentItem.id ? 'Opening...' : 'View upload'}
                  </button>
                  <button
                    type="button"
                    className="ghost-button ghost-button-danger"
                    onClick={() => onDocumentRemove(documentItem.id)}
                    disabled={!documentState.storagePath || documentRemovingId === documentItem.id}
                  >
                    {documentRemovingId === documentItem.id ? 'Removing...' : 'Remove upload'}
                  </button>
                </div>
                {documentState.storagePath && !canPreviewStoredFile(documentState.mimeType, documentState.fileName) ? (
                  <p className="field-hint">
                    This file type can be stored and reviewed, but inline preview is currently limited to PDFs and images.
                  </p>
                ) : null}

                <div className="document-note">
                  <span className="meta-label">Stored review note</span>
                  <p>
                    {documentState.note ||
                      'Once uploaded, ChemCab compliance can verify the document without exposing it to dispatch.'}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function CertificatesPage({ state, trainingModules = TRAINING_MODULES }) {
  const certificates = trainingModules.filter(
    (module) => state.modules?.[module.id]?.status === 'passed'
  ).map((module) => ({
    module,
    certificateId: state.modules[module.id]?.certificateId || '',
    issuedAt: state.modules[module.id]?.passedAt || '',
    expiresOn: state.modules[module.id]?.expiresOn || '',
  }));

  const history = Array.isArray(state.history) ? state.history : [];

  return (
    <div className="page-stack">
      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Certificates</p>
            <h3>Current training certificates</h3>
          </div>
        </div>
        {certificates.length ? (
          <div className="certificate-grid">
            {certificates.map((entry) => (
              <article className="certificate-card" key={entry.certificateId || entry.module.id}>
                <div className="certificate-ribbon">Active</div>
                <h4>{entry.module.certificateName}</h4>
                <p>{entry.module.subtitle}</p>
                <div className="certificate-meta">
                  <span>Certificate ID</span>
                  <strong>{entry.certificateId}</strong>
                </div>
                <div className="certificate-meta">
                  <span>Issued</span>
                  <strong>{formatDateShort(entry.issuedAt)}</strong>
                </div>
                <div className="certificate-meta">
                  <span>Refresh due</span>
                  <strong>{formatDateShort(entry.expiresOn)}</strong>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="muted-copy">
            Pass a module to generate your first current certificate.
          </p>
        )}
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">History</p>
            <h3>Certification and review history</h3>
          </div>
        </div>
        <div className="history-list">
          {history.map((entry) => (
            <article className="history-item" key={entry.id}>
              <div className="history-dot" />
              <div>
                <strong>{entry.title}</strong>
                <p>{entry.detail}</p>
                <span>{formatDateTime(entry.at)}</span>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function LoginScreen({ busy, error, onLogin }) {
  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-copy">
          <p className="auth-kicker">ChemCab Driver Hub</p>
          <h1>Compliance, training, and certification in one login.</h1>
          <p>
            {HOSTED_DEV ? 'Sign in with your seeded development driver account to test compliance, training, and documents.' : 'Sign in with your ChemCab Google account to review requirements, finish modules, upload documents, and track readiness before going online in the driver app.'}
          </p>
        </div>

        <div className="auth-feature-grid">
          <article className="auth-feature">
            <strong>Clear task board</strong>
            <span>See what is done, what is pending review, and what blocks online access.</span>
          </article>
          <article className="auth-feature">
            <strong>Exam-first modules</strong>
            <span>100% score required with explanations for every incorrect answer.</span>
          </article>
          <article className="auth-feature">
            <strong>Current certificate view</strong>
            <span>Keep certificates, issue dates, and refresh timing in one place.</span>
          </article>
        </div>

        {error ? <div className="auth-error">{error}</div> : null}

        {HOSTED_DEV ? <a className="auth-button" href="/dashboard">Sign in with a development account</a> : <button className="auth-button" type="button" disabled={busy} onClick={onLogin}>
          {busy ? 'Opening Google sign-in...' : 'Continue with ChemCab Google'}
        </button>}
      </div>
    </div>
  );
}

function DriverDashboardApp({ portalUser = null, embedded = false, documentsOnly = false }) {
  const [authLoading, setAuthLoading] = useState(!portalUser);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState('');
  const [user, setUser] = useState(portalUser);
  const [activePage, setActivePage] = useState(documentsOnly ? 'profile' : 'overview');
  const [dashboardState, setDashboardState] = useState(null);
  const [trainingModules, setTrainingModules] = useState(TRAINING_MODULES);
  const [moduleLibraryLoading, setModuleLibraryLoading] = useState(true);
  const [bootstrapModuleIds, setBootstrapModuleIds] = useState(FALLBACK_BOOTSTRAP_MODULE_IDS);
  const [bootstrapPolicyLoading, setBootstrapPolicyLoading] = useState(true);
  const [documentUploadBusyId, setDocumentUploadBusyId] = useState('');
  const [documentPreviewBusyId, setDocumentPreviewBusyId] = useState('');
  const [documentRemovingId, setDocumentRemovingId] = useState('');
  const [documentPreview, setDocumentPreview] = useState(null);
  const persistenceTimerRef = useRef(null);

  useEffect(() => {
    if (portalUser) {
      setUser(portalUser);
      setAuthError('');
      setAuthLoading(false);
      return undefined;
    }
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      if (!nextUser) {
        setUser(null);
        setDashboardState(null);
        setAuthLoading(false);
        return;
      }

      const email = String(nextUser.email || '').trim().toLowerCase();
      if (!email.endsWith(`@${CHEMCAB_DOMAIN}`)) {
        await signOut(auth).catch(() => {});
        setAuthError('Use your @chemcab.com Google account to access the driver dashboard.');
        setAuthLoading(false);
        return;
      }

      setUser(nextUser);
      setAuthError('');
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [portalUser]);

  useEffect(() => {
    if (!user) return undefined;
    const email = String(user.email || '').trim().toLowerCase();
    if (!email) return undefined;
    let active = true;
    setModuleLibraryLoading(true);
    setBootstrapPolicyLoading(true);
    setAuthError('');
    fetchWithDriverAuth('/api/driver/onboarding/workspace')
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body?.message || body?.error || `Could not load onboarding workspace (${response.status}).`);
        if (!active) return;
        const nextModules = Array.isArray(body?.trainingModules)
          ? body.trainingModules
              .map((entry) => normalizeModuleDefinition(entry))
              .filter((module) => module.id && module.title)
              .sort((a, b) => a.title.localeCompare(b.title))
          : [];
        const resolvedModules = nextModules.length ? nextModules : TRAINING_MODULES;
        const nextBootstrapIds = Array.isArray(body?.defaultModuleIds) && body.defaultModuleIds.length
          ? body.defaultModuleIds
          : FALLBACK_BOOTSTRAP_MODULE_IDS;
        const remoteState = body?.workspace && typeof body.workspace === 'object' ? body.workspace : null;
        const resolvedState = reconcileDashboardStates(
          remoteState,
          readDashboardState(email),
          user,
          resolvedModules,
          DOCUMENT_LIBRARY,
          nextBootstrapIds
        );
        setTrainingModules(resolvedModules);
        setBootstrapModuleIds(nextBootstrapIds);
        setDashboardState(resolvedState);
        writeDashboardState(email, resolvedState);
      })
      .catch((error) => {
        if (!active) return;
        console.error('Could not load driver onboarding workspace:', error);
        if (String(error?.message || '').includes('driver_profile_not_found')) {
          setDashboardState(null);
          setAuthError('Onboarding & Compliance becomes available after ChemCab provisions your driver profile.');
          return;
        }
        const fallback = normalizeDashboardState(
          readDashboardState(email) || createInitialDashboardState(user, TRAINING_MODULES, DOCUMENT_LIBRARY, FALLBACK_BOOTSTRAP_MODULE_IDS),
          user,
          TRAINING_MODULES,
          DOCUMENT_LIBRARY,
          FALLBACK_BOOTSTRAP_MODULE_IDS
        );
        setTrainingModules(TRAINING_MODULES);
        setBootstrapModuleIds(FALLBACK_BOOTSTRAP_MODULE_IDS);
        setDashboardState(fallback);
        writeDashboardState(email, fallback);
        setAuthError('Could not load the shared onboarding record. Showing the last state saved on this device.');
      })
      .finally(() => {
        if (!active) return;
        setModuleLibraryLoading(false);
        setBootstrapPolicyLoading(false);
      });

    return () => { active = false; };
  }, [user]);

  useEffect(() => {
    return () => {
      if (persistenceTimerRef.current) {
        window.clearTimeout(persistenceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (documentPreview?.objectUrl) {
        window.URL.revokeObjectURL(documentPreview.objectUrl);
      }
    };
  }, [documentPreview]);

  const complianceSummary = useMemo(
    () =>
      dashboardState
        ? buildComplianceSummary(dashboardState, trainingModules, DOCUMENT_LIBRARY)
        : null,
    [dashboardState, trainingModules]
  );
  const assignedTrainingModules = useMemo(
    () => getAssignedTrainingModules(dashboardState, trainingModules),
    [dashboardState, trainingModules]
  );
  const requiredDocumentLibrary = useMemo(
    () => getRequiredDocumentLibrary(dashboardState, DOCUMENT_LIBRARY),
    [dashboardState]
  );

  const handleLogin = async () => {
    setAuthBusy(true);
    setAuthError('');
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      setAuthError(String(error?.message || error || 'Could not sign in.'));
    } finally {
      setAuthBusy(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth).catch(() => {});
    setActivePage('overview');
  };

  const persistDashboardState = async (nextState) => {
    if (!user?.email) return;
    writeDashboardState(user.email, nextState);
    try {
      await persistDashboardStateToCloud({
        state: nextState,
        user,
        trainingModules,
        documentLibrary: DOCUMENT_LIBRARY,
        bootstrapModuleIds,
      });
    } catch (error) {
      console.error('Could not persist driver dashboard state:', error);
      setAuthError(
        'Changes were saved on this device, but cloud sync failed. Try refreshing again shortly.'
      );
    }
  };

  const queueDashboardPersistence = (nextState) => {
    writeDashboardState(user?.email, nextState);
    if (persistenceTimerRef.current) {
      window.clearTimeout(persistenceTimerRef.current);
    }
    persistenceTimerRef.current = window.setTimeout(() => {
      persistenceTimerRef.current = null;
      persistDashboardState(nextState);
    }, 650);
  };

  const updateDashboardState = (updater) => {
    setDashboardState((current) => {
      if (!current) return current;
      const nextState = normalizeDashboardState(
        updater(current),
        user,
        trainingModules,
        DOCUMENT_LIBRARY,
        bootstrapModuleIds
      );
      queueDashboardPersistence(nextState);
      return nextState;
    });
  };

  const handleModuleStateChange = (module, payload) => {
    updateDashboardState((current) => {
      const currentModule = current.modules?.[module.id] || {};
      const nextModule = {
        ...currentModule,
        attempts: Number(currentModule.attempts || 0) + 1,
        lastScore: payload.score,
        bestScore: Math.max(Number(currentModule.bestScore || 0), Number(payload.score || 0)),
        lastAttemptAt: payload.attemptedAt,
        status: payload.passed ? 'passed' : 'available',
        passedAt: payload.passed ? payload.attemptedAt : currentModule.passedAt || '',
        certificateId: payload.passed
          ? makeCertificateId(module.id, payload.attemptedAt)
          : currentModule.certificateId || '',
        expiresOn: payload.passed
          ? addYearsToIsoDate(payload.attemptedAt, 3)
          : currentModule.expiresOn || '',
      };

      const event = payload.passed
        ? {
            id: `${module.id}-pass-${payload.attemptedAt}`,
            type: 'exam_passed',
            title: `${module.title} passed`,
            detail: `Scored ${payload.score}% and activated ${module.certificateName}.`,
            at: payload.attemptedAt,
          }
        : {
            id: `${module.id}-fail-${payload.attemptedAt}`,
            type: 'exam_failed',
            title: `${module.title} needs another attempt`,
            detail: `Scored ${payload.score}%. Review the explanations and retake for 100%.`,
            at: payload.attemptedAt,
          };

      return appendHistoryEntry(
        {
          ...current,
          modules: {
            ...current.modules,
            [module.id]: nextModule,
          },
        },
        event
      );
    });
  };

  const handleProfileChange = (field, value) => {
    updateDashboardState((current) => ({
      ...current,
      profile: {
        ...current.profile,
        [field]: value,
      },
    }));
  };

  const handleDocumentFieldChange = async (documentId, field, value) => {
    if (field !== 'expiresOn') return;
    try {
      const response = await fetchWithDriverAuth('/api/driver/onboarding/document-metadata', {
        method: 'PATCH',
        body: JSON.stringify({ documentId, expiresOn: value || '' }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.message || body?.error || 'Could not save the expiration date.');
      updateDashboardState((current) => ({ ...current, documents: { ...current.documents, [documentId]: serverDocumentState(body.document || {}) } }));
    } catch (error) {
      setAuthError(String(error?.message || error));
    }
  };

  const handleDocumentUpload = async (documentId, file) => {
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setAuthError(`Files must be ${MAX_UPLOAD_MB} MB or smaller.`);
      return;
    }
    const meta = getDocumentMeta(documentId, requiredDocumentLibrary);
    setAuthError('');
    setDocumentUploadBusyId(documentId);

    try {
      const base64Data = await fileToBase64(file);
      const response = await fetchWithDriverAuth('/api/driver/onboarding/document-upload', {
        method: 'POST',
        body: JSON.stringify({
          documentId,
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          base64Data,
          expiresOn: String(dashboardState?.documents?.[documentId]?.expiresOn || '').trim(),
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.error || `Upload failed (HTTP ${response.status})`);
      }

      const uploadedAt = body?.document?.uploadedAt || '';

      updateDashboardState((current) =>
        appendHistoryEntry(
          {
            ...current,
            documents: {
              ...current.documents,
              [documentId]: {
                ...current.documents[documentId],
                status: 'submitted',
                fileName: String(body?.document?.fileName || file.name),
                mimeType: String(body?.document?.mimeType || file.type || 'application/octet-stream'),
                sizeBytes: Number(body?.document?.sizeBytes || file.size) || 0,
                storagePath: String(body?.document?.storagePath || ''),
                storageBucket: String(body?.document?.storageBucket || ''),
                uploadedAt,
                reviewedAt: '',
                uploadedByEmail: String(body?.document?.uploadedByEmail || user?.email || ''),
                reviewedByEmail: '',
                note: String(
                  body?.document?.note
                  || `${meta?.reviewGroup || 'Compliance'} review pending.`
                ),
              },
            },
          },
          {
            id: `${documentId}-upload-${uploadedAt}`,
            type: 'document_uploaded',
            title: `${meta?.title || documentId} uploaded`,
            detail: `${file.name} is now queued for compliance review.`,
            at: uploadedAt,
          }
        )
      );
    } catch (error) {
      setAuthError(String(error?.message || error || 'Could not upload document.'));
    } finally {
      setDocumentUploadBusyId('');
    }
  };

  const closeDocumentPreview = () => {
    setDocumentPreview((current) => {
      if (current?.objectUrl) {
        window.URL.revokeObjectURL(current.objectUrl);
      }
      return null;
    });
    setDocumentPreviewBusyId('');
  };

  const handleDocumentPreview = async (documentId) => {
    const documentState = dashboardState?.documents?.[documentId] || {};
    const fileName = String(documentState.fileName || `${documentId}.bin`).trim();
    const mimeType = String(documentState.mimeType || '').trim();
    if (!documentState.storagePath) {
      setAuthError('This upload was not stored in the new file system yet. Please upload it again to preview it.');
      return;
    }
    setAuthError('');
    setDocumentPreviewBusyId(documentId);
    setDocumentPreview((current) => {
      if (current?.objectUrl) {
        window.URL.revokeObjectURL(current.objectUrl);
      }
      return {
        documentId,
        fileName,
        mimeType,
        objectUrl: '',
        error: '',
      };
    });
    try {
      const response = await fetchWithDriverAuth('/api/driver/onboarding/document-file', {
        method: 'POST',
        body: JSON.stringify({ documentId }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error || `Preview failed (HTTP ${response.status})`);
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      setDocumentPreview((current) => {
        if (current?.objectUrl) {
          window.URL.revokeObjectURL(current.objectUrl);
        }
        return {
          documentId,
          fileName,
          mimeType: blob.type || mimeType || 'application/octet-stream',
          objectUrl,
          error: '',
        };
      });
    } catch (error) {
      setDocumentPreview((current) => ({
        ...(current || { documentId, fileName, mimeType }),
        objectUrl: '',
        error: String(error?.message || error),
      }));
      setAuthError(String(error?.message || error || 'Could not preview document.'));
    } finally {
      setDocumentPreviewBusyId('');
    }
  };

  const handleDocumentRemove = async (documentId) => {
    setAuthError('');
    setDocumentRemovingId(documentId);
    try {
      const response = await fetchWithDriverAuth('/api/driver/onboarding/document-remove', {
        method: 'POST',
        body: JSON.stringify({ documentId }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.error || `Remove failed (HTTP ${response.status})`);
      }
      const removedAt = new Date().toISOString();
      const meta = getDocumentMeta(documentId, requiredDocumentLibrary);
      if (documentPreview?.documentId === documentId) {
        closeDocumentPreview();
      }
      updateDashboardState((current) =>
        appendHistoryEntry(
          {
            ...current,
            documents: {
              ...current.documents,
              [documentId]: {
                ...(current.documents?.[documentId] || {}),
                status: 'missing',
                fileName: '',
                mimeType: '',
                sizeBytes: 0,
                storagePath: '',
                storageBucket: '',
                uploadedAt: '',
                reviewedAt: '',
                uploadedByEmail: '',
                reviewedByEmail: '',
                note: String(body?.document?.note || 'Upload removed. Add a fresh file when ready.'),
              },
            },
          },
          {
            id: `${documentId}-removed-${removedAt}`,
            type: 'document_removed',
            title: `${meta?.title || documentId} removed`,
            detail: 'The previous upload was removed from review storage.',
            at: removedAt,
          }
        )
      );
    } catch (error) {
      setAuthError(String(error?.message || error || 'Could not remove document.'));
    } finally {
      setDocumentRemovingId('');
    }
  };

  if (authLoading || moduleLibraryLoading || bootstrapPolicyLoading) {
    return (
      <div className={embedded ? 'onboarding-workspace-loading' : 'loading-screen'}>
        <div className="loading-card">
          <strong>Loading driver dashboard...</strong>
          <p>Checking your ChemCab login, module library, and compliance workspace.</p>
        </div>
      </div>
    );
  }

  if (!user || !dashboardState || !complianceSummary) {
    if (embedded) {
      return <div className="onboarding-workspace-loading"><div className="loading-card"><strong>Onboarding workspace unavailable</strong><p>{authError || 'Sign in again to open this section.'}</p></div></div>;
    }
    return <LoginScreen busy={authBusy} error={authError} onLogin={handleLogin} />;
  }

  return (
    <div className={(embedded ? 'onboarding-workspace' : 'app-shell') + (documentsOnly ? ' legacy-documents-only' : '')}>
      <div className={embedded ? 'onboarding-workspace-frame' : 'app-frame'}>
        <header className={`topbar ${embedded ? 'onboarding-workspace-header' : ''}`}>
          <div className="brand-block">
            <p className="brand-kicker">ChemCab Driver Hub</p>
            <h1>Training and compliance dashboard</h1>
            <p className="brand-copy">
              Signed in as {user.email}. Keep documents current, pass every module at 100%, and
              stay ready to go online.
            </p>
          </div>
          <div className="topbar-actions">
            <StatusPill tone={complianceSummary.status}>
              {complianceSummary.onlineEligible ? 'Eligible to go online' : 'Online access blocked'}
            </StatusPill>
            {!embedded ? <button className="ghost-button" type="button" onClick={handleLogout}>Sign out</button> : null}
          </div>
        </header>

        <div className="progress-strip">
          <div className="progress-strip-copy">
            <strong>{complianceSummary.weightedCompletion}% complete</strong>
            <span>
              {complianceSummary.completedChecklistItems} of {complianceSummary.totalChecklistItems}{' '}
              checkpoints complete
            </span>
          </div>
          <ProgressBar value={complianceSummary.weightedCompletion} />
        </div>

        <nav className="nav-row" aria-label="Driver dashboard navigation">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`nav-pill ${activePage === item.id ? 'nav-pill-active' : ''}`}
              onClick={() => setActivePage(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <main className="content-area">
          {activePage === 'overview' ? (
            <OverviewPage
              user={user}
              state={dashboardState}
              summary={complianceSummary}
              onNavigate={setActivePage}
              trainingModules={assignedTrainingModules}
              documentLibrary={requiredDocumentLibrary}
            />
          ) : null}
          {activePage === 'training' ? (
            <TrainingPage
              state={dashboardState}
              trainingModules={assignedTrainingModules}
              onModuleStateChange={handleModuleStateChange}
            />
          ) : null}
          {activePage === 'profile' ? (
            <ProfilePage
              state={dashboardState}
              summary={complianceSummary}
              documentLibrary={requiredDocumentLibrary}
              onProfileChange={handleProfileChange}
              onDocumentUpload={handleDocumentUpload}
              onDocumentFieldChange={handleDocumentFieldChange}
              onDocumentPreview={handleDocumentPreview}
              onDocumentRemove={handleDocumentRemove}
              documentUploadBusyId={documentUploadBusyId}
              documentPreviewBusyId={documentPreviewBusyId}
              documentRemovingId={documentRemovingId}
              lockOperationalFields={embedded}
            />
          ) : null}
          {activePage === 'certificates' ? (
            <CertificatesPage state={dashboardState} trainingModules={assignedTrainingModules} />
          ) : null}
        </main>
        {documentPreview ? (
          <div className="document-preview-overlay" role="dialog" aria-modal="true">
            <div className="document-preview-card">
              <div className="document-preview-head">
                <div>
                  <p className="panel-kicker">Upload preview</p>
                  <h3>{documentPreview.fileName || 'Uploaded document'}</h3>
                </div>
                <button type="button" className="ghost-button" onClick={closeDocumentPreview}>
                  Close
                </button>
              </div>
              {documentPreview.objectUrl ? (
                <p className="muted-copy">
                  <a href={documentPreview.objectUrl} target="_blank" rel="noopener noreferrer" download={documentPreview.fileName || 'document'}>Open or download document</a>
                  {' · If the preview is blank, use this link.'}
                </p>
              ) : null}
              {documentPreview.error ? (
                <p className="banner-error">{documentPreview.error}</p>
              ) : !documentPreview.objectUrl ? (
                <p className="muted-copy">Opening preview...</p>
              ) : String(documentPreview.mimeType || '').toLowerCase().startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(getFileExtension(documentPreview.fileName)) ? (
                <img
                  className="document-preview-media"
                  src={documentPreview.objectUrl}
                  alt={documentPreview.fileName || 'Uploaded document'}
                />
              ) : String(documentPreview.mimeType || '').toLowerCase() === 'application/pdf' || getFileExtension(documentPreview.fileName) === 'pdf' ? (
                <iframe
                  className="document-preview-frame"
                  src={documentPreview.objectUrl}
                  title={documentPreview.fileName || 'Uploaded document'}
                />
              ) : (
                <p className="muted-copy">
                  Inline preview is currently available for PDFs and images. This file can still be reviewed from Liveboard after upload.
                </p>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function InvitePacketApp({ inviteToken, enrollmentUser = null }) {
  const [inviteLoading, setInviteLoading] = useState(true);
  const [inviteError, setInviteError] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [form, setForm] = useState({
    legalName: '',
    personalEmail: '',
    homeAddress: '',
    homeAddressLine2: '',
    birthDate: '',
    driverLicenseNumber: '',
    driverLicenseState: '',
    driverLicenseExpiration: '',
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    vin: '',
    plateNumber: '',
    registrationExpiration: '',
    teamName: '',
    primaryGeofence: '',
    secondaryGeofence: '',
  });
  const homeAddressInputRef = useRef(null);
  const [placesReady, setPlacesReady] = useState(false);
  const [placesError, setPlacesError] = useState('');
  const [homeAddressPlaceMeta, setHomeAddressPlaceMeta] = useState(null);
  const [homeAddressTouched, setHomeAddressTouched] = useState(false);

  const [insuranceFile, setInsuranceFile] = useState(null);
  const [driverLicenseFrontFile, setDriverLicenseFrontFile] = useState(null);
  const [driverLicenseBackFile, setDriverLicenseBackFile] = useState(null);
  const [vehiclePhotos, setVehiclePhotos] = useState([]);
  const [consents, setConsents] = useState({
    backgroundScreening: false,
    drugTesting: false,
  });
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submissionReceipt, setSubmissionReceipt] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const draftStorageKey = `${DASHBOARD_STORAGE_PREFIX}:invite:${inviteToken}`;

  useEffect(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(draftStorageKey) || 'null');
      if (saved?.form) setForm((current) => ({ ...current, ...saved.form }));
      if (saved?.consents) setConsents((current) => ({ ...current, ...saved.consents }));
      if (saved?.homeAddressPlaceMeta) setHomeAddressPlaceMeta(saved.homeAddressPlaceMeta);
    } catch (_error) {
      // A malformed local draft should never block a secure invite.
    } finally {
      setDraftRestored(true);
    }
  }, [draftStorageKey]);

  useEffect(() => {
    if (!draftRestored || submissionReceipt) return undefined;
    const timer = window.setTimeout(() => {
      window.localStorage.setItem(draftStorageKey, JSON.stringify({
        form,
        consents,
        homeAddressPlaceMeta,
        savedAt: new Date().toISOString(),
      }));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [consents, draftRestored, draftStorageKey, form, homeAddressPlaceMeta, submissionReceipt]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const places = await ensurePlacesAutocompleteReady();
      if (!mounted) return;
      if (!GOOGLE_MAPS_API_KEY) {
        setPlacesError('Address lookup is unavailable. Missing REACT_APP_GOOGLE_MAPS_API_KEY.');
        return;
      }
      if (!places || !window.google?.maps?.places?.Autocomplete) {
        setPlacesError('Could not load Google Places. Refresh or contact drivers@chemcab.com.');
        return;
      }
      setPlacesReady(true);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/public/onboarding/invite/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ inviteToken }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          const errorCode = String(body?.error || '').trim().toLowerCase();
          if (errorCode === 'invite_expired') {
            throw new Error('This onboarding invite has expired. Please request a new invite from ChemCab.');
          }
          if (errorCode === 'invite_already_used') {
            throw new Error('This onboarding invite has already been used. Please contact ChemCab support.');
          }
          if (errorCode === 'invite_revoked') {
            throw new Error('This onboarding invite was revoked. Please contact ChemCab support.');
          }
          if (
            errorCode === 'onboarding_encryption_not_configured' ||
            errorCode === 'onboarding_encryption_public_key_missing'
          ) {
            throw new Error(
              'This invite cannot be opened yet because secure onboarding encryption is not configured.'
            );
          }
          throw new Error(body?.error || `Invite validation failed (${response.status})`);
        }

        if (!mounted) return;
        setInviteData(body);
        setForm((prev) => ({
          ...prev,
          personalEmail: body?.invite?.recipientEmail || '',
          teamName: body?.defaults?.teamName || prev.teamName,
          primaryGeofence: body?.defaults?.primaryGeofence || prev.primaryGeofence,
          secondaryGeofence: body?.defaults?.secondaryGeofence || prev.secondaryGeofence,
        }));
      } catch (error) {
        if (!mounted) return;
        setInviteError(String(error?.message || error));
      } finally {
        if (mounted) setInviteLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [inviteToken]);

  useEffect(() => {
    if (inviteLoading || inviteError || !placesReady || !homeAddressInputRef.current) return undefined;

    let canceled = false;
    const autocomplete = new window.google.maps.places.Autocomplete(homeAddressInputRef.current, {
      componentRestrictions: { country: 'us' },
      fields: ['formatted_address', 'place_id', 'geometry', 'name'],
    });

    autocomplete.addListener('place_changed', () => {
      if (canceled) return;
      const place = autocomplete.getPlace();
      const rawInput = String(homeAddressInputRef.current?.value || '').trim();
      const gmaps = window.google;

      const commitSelection = (finalAddress, lat, lng) => {
        const nextAddress = String(finalAddress || rawInput).trim();
        setForm((prev) => ({ ...prev, homeAddress: nextAddress }));
        setHomeAddressPlaceMeta({
          formattedAddress: nextAddress,
          placeId: place?.place_id || '',
          name: place?.name || '',
          lat: typeof lat === 'number' ? lat : null,
          lng: typeof lng === 'number' ? lng : null,
        });
        setHomeAddressTouched(true);
      };

      if (gmaps?.maps?.Geocoder) {
        const geocoder = new gmaps.maps.Geocoder();
        geocoder.geocode({ address: rawInput }, (results, status) => {
          if (canceled) return;
          if (status === 'OK' && results?.[0]) {
            const match = results[0];
            const location = match.geometry?.location;
            commitSelection(
              match.formatted_address || rawInput,
              location ? location.lat() : null,
              location ? location.lng() : null
            );
            return;
          }

          const fallbackLocation = place?.geometry?.location;
          commitSelection(
            place?.formatted_address || rawInput,
            fallbackLocation ? fallbackLocation.lat() : null,
            fallbackLocation ? fallbackLocation.lng() : null
          );
        });
      } else {
        const location = place?.geometry?.location;
        commitSelection(
          place?.formatted_address || rawInput,
          location ? location.lat() : null,
          location ? location.lng() : null
        );
      }
    });

    return () => {
      canceled = true;
      if (window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [inviteError, inviteLoading, placesReady]);

  const lockedInviteFields = useMemo(
    () => ({
      personalEmail: Boolean(inviteData?.invite?.recipientEmail),
      teamName: Boolean(inviteData?.defaults?.teamName),
      primaryGeofence: Boolean(inviteData?.defaults?.primaryGeofence),
      secondaryGeofence: Boolean(inviteData?.defaults?.secondaryGeofence),
    }),
    [inviteData]
  );

  const driverLicenseNumberIsValid = useMemo(
    () => isValidDriverLicenseNumber(form.driverLicenseNumber),
    [form.driverLicenseNumber]
  );
  const driverLicenseStateIsValid = useMemo(
    () => isValidStateCode(form.driverLicenseState),
    [form.driverLicenseState]
  );
  const hasSelectedHomeAddress = useMemo(
    () => Boolean(homeAddressPlaceMeta?.placeId),
    [homeAddressPlaceMeta]
  );

  const validationIssues = useMemo(() => {
    const issues = [];
    if (!String(form.legalName || '').trim()) issues.push('Legal name is required.');
    if (!String(form.personalEmail || '').trim()) issues.push('Personal email is required.');
    if (!String(form.homeAddress || '').trim()) issues.push('Home address is required.');
    if (!hasSelectedHomeAddress) issues.push('Select your home address from Google suggestions.');
    if (!String(form.birthDate || '').trim()) issues.push('Birth date is required.');
    if (!driverLicenseNumberIsValid) issues.push('Driver license number is required.');
    if (!driverLicenseStateIsValid) issues.push('Driver license issuing state is required.');
    if (!String(form.driverLicenseExpiration || '').trim()) issues.push('Driver license expiration date is required.');
    if (!String(form.plateNumber || '').trim()) issues.push('Plate number is required.');
    if (!String(form.vehicleYear || '').trim()) issues.push('Vehicle year is required.');
    if (!String(form.vehicleMake || '').trim()) issues.push('Vehicle make is required.');
    if (!String(form.vehicleModel || '').trim()) issues.push('Vehicle model is required.');
    if (!String(form.vin || '').trim()) issues.push('VIN is required.');
    if (!String(form.registrationExpiration || '').trim()) issues.push('Registration expiration is required.');
    if (!String(form.teamName || '').trim()) issues.push('Team is required.');
    if (!String(form.primaryGeofence || '').trim()) issues.push('Primary geofence is required.');
    if (!insuranceFile) issues.push('Proof of current insurance is required.');
    if (!driverLicenseFrontFile) issues.push('Driver license front image is required.');
    if (!driverLicenseBackFile) issues.push('Driver license back image is required.');
    if (!consents.backgroundScreening) issues.push('Background screening consent is required.');
    if (!consents.drugTesting) issues.push('Drug testing consent is required.');
    return issues;
  }, [
    consents.backgroundScreening,
    consents.drugTesting,
    driverLicenseBackFile,
    driverLicenseFrontFile,
    driverLicenseNumberIsValid,
    driverLicenseStateIsValid,
    form.birthDate,
    form.driverLicenseExpiration,
    form.homeAddress,
    form.legalName,
    form.personalEmail,
    form.plateNumber,
    form.primaryGeofence,
    form.registrationExpiration,
    form.teamName,
    form.vehicleMake,
    form.vehicleModel,
    form.vehicleYear,
    form.vin,
    hasSelectedHomeAddress,
    insuranceFile,
  ]);

  const requiredReady = validationIssues.length === 0;
  const completionPercent = Math.max(8, Math.round(((21 - Math.min(21, validationIssues.length)) / 21) * 100));

  const homeAddressMessage = useMemo(() => {
    if (!placesError && !homeAddressTouched) {
      return 'Start typing and choose a Google suggestion so we can standardize your address.';
    }
    if (placesError) return placesError;
    if (!form.homeAddress.trim()) return 'Home address is required.';
    if (!hasSelectedHomeAddress) return 'Please select a suggested address from Google Places.';
    return 'Address selected.';
  }, [form.homeAddress, hasSelectedHomeAddress, homeAddressTouched, placesError]);

  const validateFiles = (files) => {
    for (let index = 0; index < files.length; index += 1) {
      if (files[index].size > MAX_UPLOAD_BYTES) {
        return `File "${files[index].name}" exceeds ${MAX_UPLOAD_MB}MB limit.`;
      }
    }
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitBusy || !inviteData) return;

    setSubmitError('');
    setSubmitSuccess('');
    if (!hasSelectedHomeAddress) setHomeAddressTouched(true);
    if (placesError) {
      setSubmitError(placesError);
      return;
    }
    if (!requiredReady) {
      setSubmitError(validationIssues[0] || 'Complete all required fields before submitting.');
      return;
    }

    setSubmitBusy(true);
    try {
      const files = [insuranceFile, driverLicenseFrontFile, driverLicenseBackFile, ...vehiclePhotos].filter(
        Boolean
      );
      const fileValidationError = validateFiles(files);
      if (fileValidationError) throw new Error(fileValidationError);

      const normalizedHomeAddress = form.homeAddress.trim();
      const normalizedHomeAddressLine2 = form.homeAddressLine2.trim();
      const homeAddressFull = joinAddressLines(normalizedHomeAddress, normalizedHomeAddressLine2);
      const registrationExpirationMonth = String(form.registrationExpiration || '').slice(0, 7);

      const payload = {
        legalName: form.legalName.trim(),
        personalEmail: form.personalEmail.trim().toLowerCase(),
        homeAddress: normalizedHomeAddress,
        homeAddressLine2: normalizedHomeAddressLine2 || '',
        homeAddressFull,
        homeAddressPlace: {
          placeId: homeAddressPlaceMeta?.placeId || '',
          formattedAddress: homeAddressPlaceMeta?.formattedAddress || normalizedHomeAddress,
          name: homeAddressPlaceMeta?.name || '',
          lat: homeAddressPlaceMeta?.lat ?? null,
          lng: homeAddressPlaceMeta?.lng ?? null,
        },
        birthDate: normalizeDate(form.birthDate),
        driverLicense: {
          number: form.driverLicenseNumber.trim(),
          issuingState: form.driverLicenseState.trim(),
          expirationDate: normalizeDate(form.driverLicenseExpiration),
        },
        vehicle: {
          year: form.vehicleYear.trim(),
          make: form.vehicleMake.trim(),
          model: form.vehicleModel.trim(),
          vin: form.vin.trim(),
          plateNumber: form.plateNumber.trim(),
          registrationExpirationMonth,
          registrationExpiration: registrationExpirationMonth,
        },
        team: {
          teamName: form.teamName.trim(),
          primaryGeofence: form.primaryGeofence.trim(),
          secondaryGeofence: form.secondaryGeofence.trim(),
        },
        consents: {
          backgroundScreening: true,
          drugTesting: true,
        },
        attachments: {
          insuranceProofFileName: insuranceFile?.name || null,
          driverLicenseFrontFileName: driverLicenseFrontFile?.name || null,
          driverLicenseBackFileName: driverLicenseBackFile?.name || null,
          vehiclePhotoNames: vehiclePhotos.map((entry) => entry.name),
        },
      };

      const encryptedPackage = await encryptSubmissionPackage({
        publicKeyPem: inviteData?.encryption?.publicKeyPem,
        keyId: inviteData?.encryption?.keyId,
        inviteId: inviteData?.invite?.id,
        payload,
        files,
      });

      const enrollmentToken = enrollmentUser ? await enrollmentUser.getIdToken(true) : '';
      const response = await fetch(`${API_BASE}/api/public/onboarding/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(enrollmentToken ? { Authorization: `Bearer ${enrollmentToken}` } : {}),
        },
        body: JSON.stringify({
          inviteToken,
          inviteId: inviteData?.invite?.id || null,
          encryptedPackage,
          metadata: {
            recipientEmail:
              inviteData?.invite?.recipientEmail || form.personalEmail.trim().toLowerCase(),
            teamName: form.teamName.trim(),
            primaryGeofence: form.primaryGeofence.trim(),
            secondaryGeofence: form.secondaryGeofence.trim(),
          },
        }),
      });
      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errorCode = String(body?.error || '').trim().toLowerCase();
        if (
          errorCode === 'onboarding_storage_bucket_not_found' ||
          errorCode === 'onboarding_storage_bucket_not_configured'
        ) {
          throw new Error('Onboarding file storage is temporarily unavailable. Please contact drivers@chemcab.com.');
        }
        throw new Error(body?.error || `Submission failed (${response.status})`);
      }

      const receipt = {
        submissionId: body?.submissionId || 'pending',
        submittedAt: body?.submittedAt || null,
      };
      setSubmissionReceipt(receipt);
      setSubmitSuccess('Submitted securely.');
      window.localStorage.removeItem(draftStorageKey);
      window.localStorage.setItem(INVITE_STORAGE_KEY, JSON.stringify(receipt));
    } catch (error) {
      setSubmitError(String(error?.message || error));
    } finally {
      setSubmitBusy(false);
    }
  };

  return (
    <div className="invite-shell">
      <div className="invite-frame">
        <header className="invite-hero">
          <div>
            <p className="brand-kicker">ChemCab Driver Onboarding</p>
            <h1>Secure invite packet</h1>
            <p className="brand-copy">
              Complete your starting packet here. Sensitive onboarding payloads are encrypted before
              they leave the browser.
            </p>
          </div>
          <div className="invite-hero-badges">
            <StatusPill tone="blue">Invite-only access</StatusPill>
            <StatusPill tone="green">Encrypted before upload</StatusPill>
          </div>
        </header>

        <div className="onboarding-progress" aria-label={`Onboarding ${completionPercent}% complete`}>
          <div className="onboarding-progress-copy"><strong>{completionPercent}% complete</strong><span>Draft saves automatically on this device</span></div>
          <div className="onboarding-progress-track"><span style={{ width: `${completionPercent}%` }} /></div>
          <div className="onboarding-steps"><span className={form.legalName && form.homeAddress ? 'done' : 'active'}>1. Identity</span><span className={form.vin && form.plateNumber ? 'done' : ''}>2. Vehicle</span><span className={insuranceFile && driverLicenseFrontFile && driverLicenseBackFile ? 'done' : ''}>3. Documents</span><span className={requiredReady ? 'active' : ''}>4. Review</span></div>
        </div>

        <main className="invite-body">
          {inviteLoading ? <div className="banner">Validating invite...</div> : null}
          {inviteError ? <div className="banner banner-error">{inviteError}</div> : null}
          {submitError ? <div className="banner banner-error">{submitError}</div> : null}
          {submitSuccess ? <div className="banner banner-success">{submitSuccess}</div> : null}

          {!inviteLoading && !inviteError && submissionReceipt ? (
            <section className="invite-success-card">
              <h2>Thanks, your onboarding packet was submitted.</h2>
              <p>
                ChemCab Support has received your encrypted packet and will review it before
                activating your ChemCab dashboard access.
              </p>
              <p>
                Reference ID: <strong>{submissionReceipt.submissionId}</strong>
              </p>
              <p>
                Received: <strong>{formatEvidenceTime(submissionReceipt.submittedAt)}</strong>
              </p>
            </section>
          ) : null}

          {!inviteLoading && !inviteError && !submissionReceipt ? (
            <form className="invite-form" onSubmit={handleSubmit}>
              <section className="panel">
                <div className="panel-header">
                  <div>
                    <p className="panel-kicker">Identity and vehicle</p>
                    <h3>Required packet details</h3>
                  </div>
                </div>

                <div className="profile-grid">
                  <label className="field">
                    <span>Legal name *</span>
                    <input value={form.legalName} onChange={(event) => setForm((prev) => ({ ...prev, legalName: event.target.value }))} />
                  </label>
                  <label className="field">
                    <span>Personal email *</span>
                    <input
                      type="email"
                      value={form.personalEmail}
                      onChange={(event) => setForm((prev) => ({ ...prev, personalEmail: event.target.value }))}
                      readOnly={lockedInviteFields.personalEmail}
                      className={lockedInviteFields.personalEmail ? 'field-readonly' : ''}
                    />
                  </label>
                  <label className="field field-span-2">
                    <span>Home address *</span>
                    <input
                      ref={homeAddressInputRef}
                      value={form.homeAddress}
                      onChange={(event) => {
                        setForm((prev) => ({ ...prev, homeAddress: event.target.value }));
                        setHomeAddressPlaceMeta(null);
                        setHomeAddressTouched(true);
                      }}
                      onBlur={() => setHomeAddressTouched(true)}
                      placeholder="Start typing and choose your address"
                      autoComplete="off"
                    />
                    <small className={`field-hint ${hasSelectedHomeAddress ? '' : 'field-hint-warn'}`}>
                      {homeAddressMessage}
                    </small>
                  </label>
                  <label className="field">
                    <span>Address line 2</span>
                    <input
                      value={form.homeAddressLine2}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, homeAddressLine2: event.target.value }))
                      }
                      placeholder="Apt / Suite / Unit"
                    />
                  </label>
                  <label className="field">
                    <span>Birth date *</span>
                    <input
                      type="date"
                      value={form.birthDate}
                      onChange={(event) => setForm((prev) => ({ ...prev, birthDate: event.target.value }))}
                    />
                  </label>
                  <label className="field">
                    <span>Driver license number *</span>
                    <input
                      value={form.driverLicenseNumber}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          driverLicenseNumber: normalizeDriverLicenseNumberInput(event.target.value),
                        }))
                      }
                      placeholder="D1234567"
                    />
                  </label>
                  <label className="field">
                    <span>DL state *</span>
                    <input
                      value={form.driverLicenseState}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          driverLicenseState: normalizeStateCode(event.target.value),
                        }))
                      }
                      placeholder="TX"
                      maxLength={2}
                    />
                  </label>
                  <label className="field">
                    <span>Driver license expiration *</span>
                    <input
                      type="date"
                      value={form.driverLicenseExpiration}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, driverLicenseExpiration: event.target.value }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Plate number *</span>
                    <input
                      value={form.plateNumber}
                      onChange={(event) => setForm((prev) => ({ ...prev, plateNumber: event.target.value }))}
                    />
                  </label>
                  <label className="field">
                    <span>Vehicle year *</span>
                    <input
                      value={form.vehicleYear}
                      onChange={(event) => setForm((prev) => ({ ...prev, vehicleYear: event.target.value }))}
                    />
                  </label>
                  <label className="field">
                    <span>Vehicle make *</span>
                    <input
                      value={form.vehicleMake}
                      onChange={(event) => setForm((prev) => ({ ...prev, vehicleMake: event.target.value }))}
                    />
                  </label>
                  <label className="field">
                    <span>Vehicle model *</span>
                    <input
                      value={form.vehicleModel}
                      onChange={(event) => setForm((prev) => ({ ...prev, vehicleModel: event.target.value }))}
                    />
                  </label>
                  <label className="field">
                    <span>VIN *</span>
                    <input
                      value={form.vin}
                      onChange={(event) => setForm((prev) => ({ ...prev, vin: event.target.value }))}
                    />
                  </label>
                  <label className="field">
                    <span>Registration expiration *</span>
                    <input
                      type="month"
                      value={form.registrationExpiration}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, registrationExpiration: event.target.value }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Team *</span>
                    <input
                      value={form.teamName}
                      onChange={(event) => setForm((prev) => ({ ...prev, teamName: event.target.value }))}
                      readOnly={lockedInviteFields.teamName}
                      className={lockedInviteFields.teamName ? 'field-readonly' : ''}
                    />
                  </label>
                  <label className="field">
                    <span>Primary geofence *</span>
                    <input
                      value={form.primaryGeofence}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, primaryGeofence: event.target.value }))
                      }
                      readOnly={lockedInviteFields.primaryGeofence}
                      className={lockedInviteFields.primaryGeofence ? 'field-readonly' : ''}
                    />
                  </label>
                  <label className="field">
                    <span>Secondary geofence</span>
                    <input
                      value={form.secondaryGeofence}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, secondaryGeofence: event.target.value }))
                      }
                      readOnly={lockedInviteFields.secondaryGeofence}
                      className={lockedInviteFields.secondaryGeofence ? 'field-readonly' : ''}
                    />
                  </label>
                </div>
              </section>

              <section className="panel">
                <div className="panel-header">
                  <div>
                    <p className="panel-kicker">Uploads</p>
                    <h3>Required compliance documents</h3>
                  </div>
                </div>

                <div className="document-grid">
                  <article className="document-card">
                    <div className="document-card-head">
                      <div>
                        <h4>Proof of current insurance *</h4>
                        <p>Upload a PDF or image file.</p>
                      </div>
                    </div>
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      onChange={(event) => setInsuranceFile(event.target.files?.[0] || null)}
                    />
                  </article>
                  <article className="document-card">
                    <div className="document-card-head">
                      <div>
                        <h4>Driver license front *</h4>
                        <p>Front image or PDF.</p>
                      </div>
                    </div>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(event) => setDriverLicenseFrontFile(event.target.files?.[0] || null)}
                    />
                  </article>
                  <article className="document-card">
                    <div className="document-card-head">
                      <div>
                        <h4>Driver license back *</h4>
                        <p>Back image or PDF.</p>
                      </div>
                    </div>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(event) => setDriverLicenseBackFile(event.target.files?.[0] || null)}
                    />
                  </article>
                  <article className="document-card">
                    <div className="document-card-head">
                      <div>
                        <h4>Vehicle photos</h4>
                        <p>Up to {MAX_VEHICLE_PHOTOS} photos, {MAX_UPLOAD_MB}MB each.</p>
                      </div>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(event) =>
                        setVehiclePhotos(
                          Array.from(event.target.files || []).slice(0, MAX_VEHICLE_PHOTOS)
                        )
                      }
                    />
                  </article>
                </div>

                <div className="checkbox-panel">
                  <label>
                    <input
                      type="checkbox"
                      checked={consents.backgroundScreening}
                      onChange={(event) =>
                        setConsents((prev) => ({
                          ...prev,
                          backgroundScreening: event.target.checked,
                        }))
                      }
                    />
                    <span>I consent to background screening.</span>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={consents.drugTesting}
                      onChange={(event) =>
                        setConsents((prev) => ({
                          ...prev,
                          drugTesting: event.target.checked,
                        }))
                      }
                    />
                    <span>I consent to drug testing.</span>
                  </label>
                </div>
              </section>

              <div className="invite-footer">
                {!submitBusy && validationIssues.length > 0 ? (
                  <div className="field-hint field-hint-warn">
                    Missing requirements: {validationIssues.slice(0, 3).join(' ')}
                  </div>
                ) : null}
                <button className="primary-button" type="submit" disabled={submitBusy}>
                  {submitBusy ? 'Encrypting and submitting...' : 'Submit secure onboarding'}
                </button>
              </div>

              <div className="invite-meta">
                Invite: {inviteData?.invite?.recipientEmail || 'unknown'} | Encryption key:{' '}
                {inviteData?.encryption?.keyId || 'default'}
              </div>
            </form>
          ) : null}
        </main>
      </div>
    </div>
  );
}

function ModernDriverComplianceApp(props) {
  return (
    <ComplianceWorkspace
      {...props}
      LegacyDashboardComponent={DriverDashboardApp}
    />
  );
}

function App() {
  return <PortalExperience InvitePacketComponent={InvitePacketApp} OnboardingDashboardComponent={ModernDriverComplianceApp} />;
}

export default App;
